import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      villa: 'The Villa',
      bedrooms: 'Bedrooms',
      amenities: 'Amenities',
      gallery: 'Gallery',
      booking: 'Book Now',
      reviews: 'Ratings',
      location: 'Location',
      contact: 'Contact',
      policies: 'Policies',
      more: 'More',
      host: 'The Host',
    },
    // Hero
    hero: {
      subtitle: 'Corfu, Greece',
      title: 'Villa Aristotle',
      tagline: 'Private villa with garden, near the Old Town of Corfu and beaches.',
      proximity: '10 min from Airport & Port • In Corfu Town, by the {unesco}',
      unescoLink: 'UNESCO Old Town',
    },
    // Sections
    sections: {
      welcomeTitle: 'Welcome to Villa Aristotle',
      welcomeText: 'Located on the enchanting island of Corfu, Villa Aristotle offers a peaceful escape in one of Greece\'s most beloved destinations.',
      featuresTitle: 'The Property',
      featuresSubtitle: 'What Awaits You',
      reviewsTitle: 'Guest Experiences',
      reviewsSubtitle: 'What our guests say about their stay',
      locationTitle: 'Prime Location',
      locationSubtitle: 'Corfu, Greece',
      ctaTitle: 'Enquire About Your Stay',
      ctaText: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
    },
    // Features
    features: {
      spaciousVilla: 'Spacious Villa',
      spaciousVillaDesc: 'Comfortable accommodation',
      bedrooms: 'Multiple Bedrooms',
      bedroomsDesc: 'Sleeps up to 8 guests',
      location: 'Corfu Location',
      locationDesc: 'Greek island setting',
    },
    // Booking Widget & Page
    booking: {
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      guests: 'Guests',
      guest: 'Guest',
      selectDate: 'Select date',
      checkAvailability: 'Check Availability',
      requestBooking: 'Book',
      perNight: 'per night',
      nights: 'nights',
      night: 'night',
      petQuestion: 'Traveling with a pet?',
      petNote: 'We welcome up to 1 pet, no extra charge.',
      petFee: 'Pet',
      yes: 'Yes',
      no: 'No',
      petRequired: 'Please select whether you have a pet (Yes / No).',
      vehicleQuestion: 'Second vehicle (€5/night)',
      vehicleFee: 'Second vehicle',
      total: 'Final price',
      envFee: 'Climate resilience fee (CRF)',
      envFeeShort: 'CRF',
      infoLabel: 'More information',
      envFeeInfo: 'Government climate crisis resilience fee. €15/night April–October, €4/night November–March. Mandatory by law.',
      vehicleInfo: 'Parking for one vehicle is free. A second vehicle is charged €5/night.',
      priceInfo: 'The price covers accommodation per night. The CRF and any additional services are shown separately. For nights from 26–31 Oct 2026, 16 Nov–10 Dec 2026 and 8 Jan–28 Feb 2027: −15% for 1–2 guests, −10% for 3–4 guests.',
      lengthDiscount: 'Length-of-stay discount',
      scarcityOne: 'Only 1 day left available in',
      scarcityMany: 'Only {n} days left available in',
      discount: 'Discount',
      discountCode: 'Discount code',
      apply: 'Apply',
      applied: 'Applied!',
      addOns: 'Add-ons & Experiences',
      yourDetails: 'Your Details',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Special Requests (optional)',
      submit: 'Book',
      submitting: 'Submitting...',
      success: 'Booking request submitted successfully!',
      successMessage: 'We will contact you within 24 hours to confirm your booking.',
      enquiryNotice: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
      airportNotice: 'Note: Corfu Airport will be closed 20–28 January 2027 for works. Please take this into account when planning your arrival.',
      minStayError: 'The minimum stay for these dates is {n} nights.',
      sameDayError: 'Same-day bookings are only possible until 10:00 (Greek time). Please choose a later arrival date.',
      maxGuests: 'Maximum 8 guests (9 with sofa bed on request)',
      bookYourStay: 'Book Your Stay',
      bookingSubtitle: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
      selectYourDates: 'Select Your Dates',
      priceSummary: 'Price Summary',
      selectDatesToSeePricing: 'Select dates to see pricing',
      season: 'Season',
      yourBookingEnquiry: 'Your Booking Enquiry',
      dates: 'Dates',
      estimatedTotal: 'Final price',
      subjectToConfirmation: 'confirmation within 24 hours',
      backToHome: 'Back to Home',
      noChargeYet: 'Confirmation from the host within 24 hours. You are not charged before confirmation.',
      noChargeYetLong: 'Confirmation from the host within 24 hours. You are not charged before confirmation.',
      enterCode: 'Enter code',
      discountAppliedText: 'discount applied',
      discountCodeLabel: 'Discount code (optional)',
      discountInvalid: 'This discount code is not valid. Please correct it or leave the field empty.',
      priceBeforeDiscount: 'Price before discount',
      discountCodeLine: 'Code {code}',
      enquireNow: 'Book now',
      checkAvailabilityShort: 'Check availability',
      wonNotChargedYet: 'You won\'t be charged yet',
      specialRequestsPlaceholder: 'Special requests, arrival time, etc.',
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      subtitle: 'We would love to hear from you',
      name: 'Your Name',
      email: 'Email Address',
      phone: 'Phone Number',
      subject: 'Subject',
      message: 'Your Message',
      send: 'Send Message',
      sending: 'Sending...',
      address: 'Address',
      success: 'Message sent successfully!',
      addressStreet: 'Toni Spiteri 11',
      addressCity: 'Corfu Town 49100',
      addressCountry: 'Greece',
      responseTime: 'We aim to respond within 24 hours.',
      contactInfo: 'Contact Information',
      responseTimeLabel: 'Response Time',
      responseTimeText: 'We aim to respond within 24 hours.',
      thankYou: 'Thank you for reaching out. We will respond to your message within 24 hours.',
    },
    // Gallery
    gallery: {
      title: 'Photo Gallery',
      subtitle: 'See photos of Villa Aristotle and its indoor and outdoor spaces.',
      exterior: 'Exterior',
      garden: 'Garden',
      terrace: 'Terrace',
      livingRoom: 'Living Room',
      kitchen: 'Kitchen',
      bedroom: 'Bedroom',
    },
    // Amenities
    amenities: {
      title: 'Villa Aristotle Amenities',
      internetTitle: 'Internet & Entertainment',
      wifi: 'Free Wi-Fi throughout',
      netflix: 'Netflix',
      smartTv: 'Smart TV',
      tvBedroom: 'TV in each bedroom',
      workDesk: 'Work desk',
      accommodationTitle: 'Accommodation',
      fourBedrooms: '4 bedrooms',
      eightGuests: 'Up to 8 guests',
      linens: 'Bed linens',
      towels: 'Towels',
      travelCots: '2 travel cots for babies',
      kitchenTitle: 'Kitchen',
      fullyEquipped: 'Fully equipped kitchen',
      fridge: 'Refrigerator',
      oven: 'Oven',
      stove: 'Stove',
      coffeeMaker: 'Nespresso machine (capsules), filter coffee and Greek coffee',
      kettle: 'Kettle',
      cookware: 'Cookware',
      bathroomTitle: 'Bathroom',
      privateBathrooms: '2 bathrooms (one with bathtub, one with shower)',
      showerBath: 'Shower or bathtub',
      hairDryer: 'Hair dryer',
      toiletries: 'Toiletries',
      outdoorTitle: 'Outdoor Spaces',
      privateGarden: 'Private garden (300 m²)',
      terrace: 'Terrace / balcony',
      outdoorSeating: 'Outdoor seating area',
      bbq: 'BBQ',
      practicalTitle: 'Practical Amenities',
      parking: 'Free parking for one vehicle (second vehicle €5/night)',
      safe: 'Safe',
      washingMachine: 'Washing machine',
      dryer: 'Dryer',
      airConditioning: 'Air conditioning with heating in the 3 ground-floor bedrooms; radiators throughout the house',
      heating: 'Heating',
      gym: 'Small gym (treadmill, dumbbells, resistance bands)',
      keyBox: 'Key box – self check-in at any time',
      pet: 'Pet welcome (max. 1, free of charge)',
    },
    // Location
    location: {
      title: 'Corfu, Greece',
      subtitle: 'Villa Aristotle is located on the beautiful island of Corfu, one of Greece\'s most enchanting Ionian destinations. 10 minutes by car from Corfu Airport (CFU) and the port, 10 minutes by car from the Old Town (UNESCO).',
      byAir: 'By Air',
      byAirDesc: 'Corfu International Airport (CFU)',
      byAirText: 'Direct flights from major European cities.',
      byCar: 'By Car',
      byCarDesc: 'Rental recommended',
      byCarText: 'A rental car is recommended to explore Corfu. We can assist with rental car arrangements from local providers.',
      aboutCorfu: 'Discover Corfu',
      aboutCorfuText: 'Corfu (Kerkyra) is one of the most beautiful Greek islands, known for its lush green landscapes, Venetian architecture, and stunning coastline.',
      gettingHere: 'Getting Here',
      aboutTheIsland: 'About the Island',
      discoverCorfuText: 'Corfu (Kerkyra) is one of the most beautiful Greek islands, known for its lush green landscapes, Venetian architecture, and stunning coastline. The island offers a perfect blend of natural beauty, rich history, and vibrant culture.',
      oldTownText: 'The UNESCO-listed Old Town features impressive fortresses, narrow streets, and Italian-influenced architecture. The island is known for its excellent local cuisine, crystal-clear waters, and warm hospitality.',
      openInMaps: 'Open in Google Maps',
    },
    // Policies
    policies: {
      title: 'Booking Policies',
      subtitle: 'Please review our policies before making a reservation.',
      terms: 'Terms & Conditions',
      bookingProcess: 'Booking Process',
      bookingProcessText: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
      paymentTerms: 'Payment Terms',
      paymentTermsText: 'Payment instructions are sent with the booking confirmation.',
      checkInOut: 'Check-in / Check-out',
      checkIn: 'Check-in: 16:00',
      checkOut: 'Check-out: until 10:00',
      cancellation: 'Cancellation Policy',
      cancellationText: 'Free cancellation up to 60 days before arrival (full refund). For cancellations within 60 days of arrival, the amount is not refunded.',
      modifications: 'Modifications',
      modificationsText: 'Date changes are subject to availability. Please contact us as early as possible if you need to modify your booking.',
      damageDeposit: 'Damage Deposit',
      damageDepositText: 'A €500 security deposit is required and will be refunded within 7 days of checkout.',
      houseRules: 'House Rules',
      houseRulesText: 'Specific house rules will be provided upon booking confirmation. Standard policies include no smoking indoors and respecting quiet hours.',
      noSmoking: 'No smoking indoors',
      noParties: 'No parties without prior approval',
      quietHours: 'Quiet hours: 11:00 PM - 8:00 AM',
      maxOccupancy: 'Maximum occupancy: 8 guests (9 with sofa bed on request)',
      privacy: 'PRIVACY POLICY — VILLA ARISTOTLE',
      privacyLastUpdated: 'Last updated: 5 August 2026',
      privacyWhoWeAre: '1. WHO WE ARE',
      privacyWhoWeAreText: 'Villa Aristotle is located in Corfu Town, Greece. For any matter relating to your personal data, contact: villa.aristotle.cfu@gmail.com',
      privacyWhatWeCollect: '2. WHAT DATA WE COLLECT',
      privacyWhatWeCollectText: 'When you make a booking request through our site, we collect: name, email, phone, dates of stay, number of guests, whether you are traveling with a pet, and an optional special requests field. We do not collect payment details through the site: payment is handled off-site, after your booking has been confirmed manually by us.',
      privacyWhyWeCollect: '3. WHY WE COLLECT IT',
      privacyWhyWeCollectText: 'Solely for: processing your booking, communication before/during your stay, and compliance with tax obligations (AADE) where required.',
      privacyWhoHasAccess: '4. WHO HAS ACCESS',
      privacyWhoHasAccessText: 'Your data is not sold or shared with third parties for advertising purposes. Booking management tools (channel manager) may be used solely for organising your stay.',
      privacyYourRights: '5. YOUR RIGHTS',
      privacyYourRightsText: 'You have the right to access, correct or delete your data, and the right to object to its processing, by contacting the email above. Villa Aristotle is committed to protecting your personal data in accordance with the General Data Protection Regulation (GDPR).',
      privacyCookies: '6. COOKIES & TRACKING',
      privacyCookiesText: 'Our site does not use any visitor tracking tool, analytics or third-party cookies. The fonts used for the appearance of the site are loaded from Google Fonts, which means that when a page loads Google may receive your IP address. We ourselves have no access to that data and do not use it.',
      license: 'Property License',
      licenseText: 'Villa Aristotle operates in accordance with Greek tourism accommodation regulations and holds the required operating licenses under Greek law.',
    },
    // Rules
    rules: {
      title: 'House Rules',
      checkInLabel: 'Check-in',
      checkInTime: 'From 16:00.',
      checkOutLabel: 'Check-out',
      checkOutTime: '09:30 – 10:00.',
      childrenLabel: 'Children',
      childrenText: 'Children of all ages are welcome.',
      cribsLabel: 'Baby cribs',
      cribsText: 'Available free of charge upon request.',
      smokingLabel: 'Smoking',
      smokingText: 'Not allowed indoors.',
      partiesLabel: 'Parties',
      partiesText: 'No parties or events allowed.',
      quietHoursLabel: 'Quiet hours',
      quietHoursTime: '23:00 – 08:00.',
      petsLabel: 'Pets',
      petsText: 'Up to 1 pet welcome, no extra charge.',
      parkingLabel: 'Parking',
      parkingText: 'Free parking for one vehicle. A second vehicle is charged €5/night.',
    },
    // Footer
    footer: {
      tagline: 'Private villa with garden, near the Old Town of Corfu and beaches.',
      quickLinks: 'Quick Links',
      contact: 'Contact',
      followUs: 'Follow Us',
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      cancellation: 'Cancellation Policy',
      legal: 'Legal',
      licensedProperty: 'Licensed Property',
      licenseText: 'Villa Aristotle operates in accordance with Greek tourism accommodation regulations and holds the required operating licenses under Greek law.',
      alsoAvailableOn: 'Also available on',
      theHost: 'The Host',
      hostText: 'Villa Aristotle is hosted by Christina, who personally takes care of the home and its preparation for every guest, ensuring a comfortable and peaceful stay in Corfu.',
    },
    // Common
    common: {
      viewAll: 'View All',
      learnMore: 'Learn More',
      backToTop: 'Back to Top',
      from: 'From',
      currency: '€',
      comingSoon: 'Coming Soon',
      detailsComingSoon: 'Details Coming Soon',
      enquireNow: 'Enquire Now',
      submitEnquiry: 'Submit Enquiry',
      contactUs: 'Contact Us',
      backToHome: 'Back to Home',
      viewDetails: 'View Details',
      important: 'Important',
    },
    // Reviews
    reviews: {
      title: 'Guest Ratings',
      subtitle: 'See what guests say about Villa Aristotle on major booking platforms.',
      reviewsLabel: 'reviews',
      superhost: 'Superhost (2nd time)',
      updated: 'Updated: October 2026',
    },
    // Bedrooms
    bedrooms: {
      title: 'Bedrooms',
      subtitle: 'Villa Aristotle has 4 bedrooms and accommodates up to 8 guests.',
      bedroom1: 'Bedroom 1',
      bedroom1Desc: 'On the ground floor, with a double bed, air conditioning with heating and a Smart TV with Netflix.',
      bedroom2: 'Bedroom 2',
      bedroom2Desc: 'On the ground floor, with a double bed, air conditioning with heating and a Smart TV with Netflix.',
      bedroom3: 'Bedroom 3',
      bedroom3Desc: 'On the ground floor, with a double bed, air conditioning with heating and a Smart TV with Netflix.',
      bedroom4: 'Bedroom 4',
      bedroom4Desc: 'On the lower ground floor, with 2 single beds and a small high window. The coolest room in the house, even in summer – cool air from the ground-floor air conditioners flows down there; ceiling and floor fans, no air conditioner of its own. Open access from the living room, without a door.',
      needInfo: 'Need Accommodation Information?',
      needInfoText: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
    },
    // Villa
    villa: {
      title: 'Villa Aristotle',
      welcome: 'Welcome to Villa Aristotle',
      descPart1: 'Villa Aristotle is a private villa with garden in Corfu Town, 10 minutes from the airport and port, close to the {unesco}.',
      unescoLink: 'Old Town of Corfu (a UNESCO World Heritage Site)',
      descPart2: 'The property accommodates up to 8 guests and features 4 bedrooms, a fully equipped kitchen, comfortable indoor spaces and a 300 m² private garden with outdoor relaxation areas.',
      descPart3: 'Its location combines easy access to the Old Town of Corfu and beaches with a quiet environment for accommodation.',
      quickEnquiry: 'Direct booking',
      quickEnquiryText: 'Direct booking. The price you see is the final price (CRF included). Confirmation from the host within 24 hours. You are not charged before confirmation.',
      interiorPhoto: 'Interior',
      exteriorPhoto: 'Exterior',
      viewAccommodation: 'View accommodation details',
      discoverAvailable: 'Discover what\'s available',
      exploreCorfu: 'Explore Corfu, Greece',
      bedrooms: '4 Bedrooms',
      maxGuests: 'Up to 8 guests',
    },
    // Host
    host: {
      title: 'The Host',
      textPart1: 'My name is Christina and I personally take care of Villa Aristotle, so that every guest can enjoy a comfortable, clean and peaceful stay in Corfu.',
      textPart2: 'For me, hosting means personal care, attention to detail and an authentic stay experience.',
    },
    // Toasts (μηνύματα σφάλματος που ήταν hardcoded στα components)
    toasts: {
      fillRequired: 'Please fill in all required fields',
      datesUnavailable: 'The selected dates are not available. Please choose different dates.',
      someDatesUnavailable: 'Some dates in your selection are unavailable. Please choose different dates.',
      bookingError: 'Error submitting booking request. Please try again.',
      contactError: 'Error sending message. Please try again.',
    },
    // WhatsApp
    whatsapp: {
      message: 'Hello! I would like to ask about Villa Aristotle 🌿\nDates: \nGuests: ',
    },
  },
  gr: {
    // Navigation
    nav: {
      home: 'Αρχική',
      villa: 'Η Βίλα',
      bedrooms: 'Υπνοδωμάτια',
      amenities: 'Παροχές',
      gallery: 'Συλλογή',
      booking: 'Κράτηση',
      reviews: 'Βαθμολογία',
      location: 'Τοποθεσία',
      contact: 'Επικοινωνία',
      policies: 'Πολιτικές',
      more: 'Περισσότερα',
      host: 'Η Οικοδέσποινα',
    },
    // Hero
    hero: {
      subtitle: 'Κέρκυρα, Ελλάδα',
      title: 'Villa Aristotle',
      tagline: 'Ιδιωτική βίλα με κήπο, κοντά στην Παλαιά Πόλη της Κέρκυρας και σε παραλίες.',
      proximity: '10 λεπτά από Αεροδρόμιο & Λιμάνι • Στην Πόλη της Κέρκυρας, δίπλα στην {unesco}',
      unescoLink: 'Παλαιά Πόλη UNESCO',
    },
    // Sections
    sections: {
      welcomeTitle: 'Καλώς ήρθατε στη Villa Aristotle',
      welcomeText: 'Στο γοητευτικό νησί της Κέρκυρας, η Villa Aristotle προσφέρει μια ήρεμη απόδραση σε έναν από τους πιο αγαπημένους προορισμούς της Ελλάδας.',
      featuresTitle: 'Το Ακίνητο',
      featuresSubtitle: 'Τι σας Περιμένει',
      reviewsTitle: 'Εμπειρίες Επισκεπτών',
      reviewsSubtitle: 'Τι λένε οι επισκέπτες μας για τη διαμονή τους',
      locationTitle: 'Εξαιρετική Τοποθεσία',
      locationSubtitle: 'Κέρκυρα, Ελλάδα',
      ctaTitle: 'Ρωτήστε για τη Διαμονή σας',
      ctaText: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
    },
    // Features
    features: {
      spaciousVilla: 'Ευρύχωρη Βίλα',
      spaciousVillaDesc: 'Άνετη διαμονή',
      bedrooms: 'Πολλά Υπνοδωμάτια',
      bedroomsDesc: 'Έως 8 άτομα',
      location: 'Τοποθεσία Κέρκυρας',
      locationDesc: 'Ελληνικό νησιωτικό περιβάλλον',
    },
    // Booking Widget & Page
    booking: {
      checkIn: 'Άφιξη',
      checkOut: 'Αναχώρηση',
      guests: 'Επισκέπτες',
      guest: 'Επισκέπτης',
      selectDate: 'Επιλέξτε ημερομηνία',
      checkAvailability: 'Έλεγχος Διαθεσιμότητας',
      requestBooking: 'Κράτηση',
      perNight: 'ανά διανυκτέρευση',
      nights: 'διανυκτερεύσεις',
      night: 'διανυκτέρευση',
      petQuestion: 'Έχετε κατοικίδιο;',
      petNote: 'Δεχόμαστε έως 1 κατοικίδιο, χωρίς επιπλέον χρέωση.',
      petFee: 'Κατοικίδιο',
      yes: 'Ναι',
      no: 'Όχι',
      petRequired: 'Παρακαλούμε επιλέξτε αν έχετε κατοικίδιο (Ναι / Όχι).',
      vehicleQuestion: 'Δεύτερο όχημα (5€/διανυκτέρευση)',
      vehicleFee: 'Δεύτερο όχημα',
      total: 'Τελική τιμή',
      envFee: 'Τέλος ανθεκτικότητας κλιματικής κρίσης (ΤΑΚΚ)',
      envFeeShort: 'ΤΑΚΚ',
      infoLabel: 'Περισσότερες πληροφορίες',
      envFeeInfo: 'Κρατικό τέλος ανθεκτικότητας στην κλιματική κρίση. 15€/διανυκτέρευση Απρίλιο–Οκτώβριο, 4€/διανυκτέρευση Νοέμβριο–Μάρτιο. Υποχρεωτικό βάσει νόμου.',
      vehicleInfo: 'Η στάθμευση για ένα όχημα είναι δωρεάν. Για δεύτερο όχημα ισχύει χρέωση 5€/διανυκτέρευση.',
      priceInfo: 'Η τιμή περιλαμβάνει τη διαμονή ανά διανυκτέρευση. Το ΤΑΚΚ και τυχόν πρόσθετες υπηρεσίες εμφανίζονται ξεχωριστά. Για διανυκτερεύσεις στις περιόδους 26.10–31.10.2026, 16.11–10.12.2026 και 08.01–28.02.2027: −15 % για 1–2 άτομα, −10 % για 3–4 άτομα.',
      lengthDiscount: 'Έκπτωση μακράς διαμονής',
      scarcityOne: 'Μόνο 1 ελεύθερη ημέρα για',
      scarcityMany: 'Μόνο {n} ελεύθερες ημέρες για',
      discount: 'Έκπτωση',
      discountCode: 'Κωδικός έκπτωσης',
      apply: 'Εφαρμογή',
      applied: 'Εφαρμόστηκε!',
      addOns: 'Πρόσθετες Υπηρεσίες',
      yourDetails: 'Τα Στοιχεία Σας',
      fullName: 'Ονοματεπώνυμο',
      email: 'Email',
      phone: 'Τηλέφωνο',
      message: 'Ειδικά Αιτήματα (προαιρετικά)',
      submit: 'Κράτηση',
      submitting: 'Υποβολή...',
      success: 'Το αίτημα κράτησης υποβλήθηκε επιτυχώς!',
      successMessage: 'Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών για επιβεβαίωση.',
      enquiryNotice: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      airportNotice: 'Σημείωση: Το αεροδρόμιο Κέρκυρας θα είναι κλειστό 20–28 Ιανουαρίου 2027 λόγω εργασιών. Παρακαλούμε λάβετέ το υπόψη στον προγραμματισμό της άφιξής σας.',
      minStayError: 'Η ελάχιστη διαμονή για αυτές τις ημερομηνίες είναι {n} διανυκτερεύσεις.',
      sameDayError: 'Κράτηση για την ίδια μέρα γίνεται μόνο έως τις 10:00 (ώρα Ελλάδας). Παρακαλούμε επιλέξτε μεταγενέστερη ημερομηνία άφιξης.',
      maxGuests: 'Μέγιστο 8 άτομα (9 με καναπέ-κρεβάτι κατόπιν αιτήματος)',
      bookYourStay: 'Κλείστε τη Διαμονή σας',
      bookingSubtitle: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      selectYourDates: 'Επιλέξτε τις Ημερομηνίες σας',
      priceSummary: 'Σύνοψη Τιμής',
      selectDatesToSeePricing: 'Επιλέξτε ημερομηνίες για να δείτε τιμές',
      season: 'Εποχή',
      yourBookingEnquiry: 'Το Αίτημά σας',
      dates: 'Ημερομηνίες',
      estimatedTotal: 'Τελική τιμή',
      subjectToConfirmation: 'επιβεβαίωση εντός 24 ωρών',
      backToHome: 'Επιστροφή στην Αρχική',
      noChargeYet: 'Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      noChargeYetLong: 'Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      enterCode: 'Εισάγετε κωδικό',
      discountAppliedText: 'έκπτωση εφαρμόστηκε',
      discountCodeLabel: 'Κωδικός έκπτωσης (προαιρετικό)',
      discountInvalid: 'Ο κωδικός έκπτωσης δεν είναι έγκυρος. Διορθώστε τον ή αφήστε το πεδίο κενό.',
      priceBeforeDiscount: 'Τιμή πριν την έκπτωση',
      discountCodeLine: 'Κωδικός {code}',
      enquireNow: 'Κράτηση τώρα',
      checkAvailabilityShort: 'Έλεγχος διαθεσιμότητας',
      wonNotChargedYet: 'Δεν θα χρεωθείτε ακόμα',
      specialRequestsPlaceholder: 'Ειδικά αιτήματα, ώρα άφιξης, κλπ.',
    },
    // Contact
    contact: {
      title: 'Επικοινωνήστε Μαζί Μας',
      subtitle: 'Θα χαρούμε να ακούσουμε τα νέα σας',
      name: 'Το Όνομά Σας',
      email: 'Διεύθυνση Email',
      phone: 'Τηλέφωνο',
      subject: 'Θέμα',
      message: 'Το Μήνυμά Σας',
      send: 'Αποστολή Μηνύματος',
      sending: 'Αποστολή...',
      address: 'Διεύθυνση',
      success: 'Το μήνυμα στάλθηκε επιτυχώς!',
      addressStreet: 'Τόνη Σπητέρη 11',
      addressCity: 'Πόλη Κέρκυρας 49100',
      addressCountry: 'Ελλάδα',
      responseTime: 'Απαντάμε εντός 24 ωρών.',
      contactInfo: 'Στοιχεία Επικοινωνίας',
      responseTimeLabel: 'Χρόνος Απάντησης',
      responseTimeText: 'Απαντάμε εντός 24 ωρών.',
      thankYou: 'Ευχαριστούμε που επικοινωνήσατε. Θα απαντήσουμε στο μήνυμά σας εντός 24 ωρών.',
    },
    // Gallery
    gallery: {
      title: 'Συλλογή Φωτογραφιών',
      subtitle: 'Δείτε φωτογραφίες από τη Villa Aristotle και τους εσωτερικούς και εξωτερικούς χώρους της.',
      exterior: 'Εξωτερικός Χώρος',
      garden: 'Κήπος',
      terrace: 'Βεράντα',
      livingRoom: 'Καθιστικό',
      kitchen: 'Κουζίνα',
      bedroom: 'Υπνοδωμάτιο',
    },
    // Amenities
    amenities: {
      title: 'Παροχές της Villa Aristotle',
      internetTitle: 'Internet & Ψυχαγωγία',
      wifi: 'Δωρεάν Wi-Fi σε όλους τους χώρους',
      netflix: 'Netflix',
      smartTv: 'Smart TV',
      tvBedroom: 'Τηλεόραση σε κάθε υπνοδωμάτιο',
      workDesk: 'Γραφείο εργασίας',
      accommodationTitle: 'Διαμονή',
      fourBedrooms: '4 υπνοδωμάτια',
      eightGuests: 'Έως 8 επισκέπτες',
      linens: 'Λευκά είδη',
      towels: 'Πετσέτες',
      travelCots: '2 παρκοκρέβατα για μωρά',
      kitchenTitle: 'Κουζίνα',
      fullyEquipped: 'Πλήρως εξοπλισμένη κουζίνα',
      fridge: 'Ψυγείο',
      oven: 'Φούρνος',
      stove: 'Εστίες',
      coffeeMaker: 'Καφετιέρα Nespresso (κάψουλες), καφές φίλτρου και ελληνικός',
      kettle: 'Βραστήρας',
      cookware: 'Κουζινικά σκεύη',
      bathroomTitle: 'Μπάνιο',
      privateBathrooms: '2 μπάνια (ένα με μπανιέρα, ένα με ντους)',
      showerBath: 'Ντους ή μπανιέρα',
      hairDryer: 'Σεσουάρ μαλλιών',
      toiletries: 'Προϊόντα περιποίησης',
      outdoorTitle: 'Εξωτερικοί χώροι',
      privateGarden: 'Ιδιωτικός κήπος (300 τ.μ.)',
      terrace: 'Βεράντα / μπαλκόνι',
      outdoorSeating: 'Εξωτερικός χώρος καθιστικού',
      bbq: 'BBQ',
      practicalTitle: 'Πρακτικές παροχές',
      parking: 'Δωρεάν στάθμευση για ένα όχημα (δεύτερο όχημα 5€/διανυκτέρευση)',
      safe: 'Χρηματοκιβώτιο',
      washingMachine: 'Πλυντήριο ρούχων',
      dryer: 'Στεγνωτήριο',
      airConditioning: 'Κλιματισμός με θέρμανση στα 3 υπνοδωμάτια του ισογείου· σώματα θέρμανσης σε όλο το σπίτι',
      heating: 'Θέρμανση',
      gym: 'Μικρό γυμναστήριο (διάδρομος, βαράκια, λάστιχα)',
      keyBox: 'Κουτί κλειδιών — αυτόνομη άφιξη οποιαδήποτε ώρα',
      pet: 'Κατοικίδιο δεκτό (έως 1, χωρίς χρέωση)',
    },
    // Location
    location: {
      title: 'Κέρκυρα, Ελλάδα',
      subtitle: 'Η Villa Aristotle βρίσκεται στο όμορφο νησί της Κέρκυρας, έναν από τους πιο γοητευτικούς Ιόνιους προορισμούς. 10 λεπτά με αυτοκίνητο από το αεροδρόμιο CFU και το λιμάνι, 10 λεπτά με αυτοκίνητο από την Παλαιά Πόλη (UNESCO).',
      byAir: 'Αεροπορικώς',
      byAirDesc: 'Αεροδρόμιο Κέρκυρας (CFU)',
      byAirText: 'Απευθείας πτήσεις από μεγάλες ευρωπαϊκές πόλεις.',
      byCar: 'Με Αυτοκίνητο',
      byCarDesc: 'Συνιστάται ενοικίαση',
      byCarText: 'Συνιστάται ενοικίαση αυτοκινήτου για εξερεύνηση. Μπορούμε να βοηθήσουμε με τη διευθέτηση.',
      aboutCorfu: 'Ανακαλύψτε την Κέρκυρα',
      aboutCorfuText: 'Η Κέρκυρα είναι ένα από τα ομορφότερα ελληνικά νησιά, γνωστό για τα καταπράσινα τοπία, τη βενετσιάνικη αρχιτεκτονική και τις εκπληκτικές ακτές.',
      gettingHere: 'Πώς να Φτάσετε',
      aboutTheIsland: 'Σχετικά με το Νησί',
      discoverCorfuText: 'Η Κέρκυρα είναι ένα από τα ομορφότερα ελληνικά νησιά, γνωστό για τα καταπράσινα τοπία, τη βενετσιάνικη αρχιτεκτονική και τις εκπληκτικές ακτές. Το νησί προσφέρει έναν τέλειο συνδυασμό φυσικής ομορφιάς, πλούσιας ιστορίας και ζωντανού πολιτισμού.',
      oldTownText: 'Η παλιά πόλη, μνημείο UNESCO, διαθέτει εντυπωσιακά φρούρια, στενά δρομάκια και αρχιτεκτονική ιταλικής επιρροής. Το νησί είναι γνωστό για την εξαιρετική τοπική κουζίνα, τα κρυστάλλινα νερά και τη ζεστή φιλοξενία.',
      openInMaps: 'Άνοιγμα στο Google Maps',
    },
    // Policies
    policies: {
      title: 'Πολιτικές Κράτησης',
      subtitle: 'Παρακαλούμε διαβάστε τις πολιτικές μας πριν την κράτηση.',
      terms: 'Όροι & Προϋποθέσεις',
      bookingProcess: 'Διαδικασία Κράτησης',
      bookingProcessText: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      paymentTerms: 'Όροι Πληρωμής',
      paymentTermsText: 'Οι οδηγίες πληρωμής αποστέλλονται με την επιβεβαίωση της κράτησης.',
      checkInOut: 'Άφιξη / Αναχώρηση',
      checkIn: 'Check-in: 16:00',
      checkOut: 'Αναχώρηση: έως 10:00',
      cancellation: 'Πολιτική Ακύρωσης',
      cancellationText: 'Δωρεάν ακύρωση έως 60 ημέρες πριν την άφιξη (πλήρης επιστροφή). Σε ακύρωση εντός 60 ημερών δεν επιστρέφεται το ποσό.',
      modifications: 'Τροποποιήσεις',
      modificationsText: 'Οι αλλαγές ημερομηνιών υπόκεινται σε διαθεσιμότητα. Παρακαλούμε επικοινωνήστε μαζί μας όσο το δυνατόν νωρίτερα αν χρειάζεται να τροποποιήσετε την κράτησή σας.',
      damageDeposit: 'Εγγύηση',
      damageDepositText: 'Απαιτείται εγγύηση 500€ που επιστρέφεται εντός 7 ημερών μετά την αναχώρηση.',
      houseRules: 'Κανόνες Σπιτιού',
      houseRulesText: 'Συγκεκριμένοι κανόνες θα παρέχονται κατά την επιβεβαίωση. Βασικές πολιτικές περιλαμβάνουν απαγόρευση καπνίσματος εσωτερικά και τήρηση ωρών ησυχίας.',
      noSmoking: 'Απαγορεύεται το κάπνισμα εσωτερικά',
      noParties: 'Χωρίς πάρτι χωρίς έγκριση',
      quietHours: 'Ώρες ησυχίας: 23:00 - 08:00',
      maxOccupancy: 'Μέγιστη χωρητικότητα: 8 άτομα (9 με καναπέ-κρεβάτι κατόπιν αιτήματος)',
      privacy: 'ΠΟΛΙΤΙΚΗ ΑΠΟΡΡΗΤΟΥ — VILLA ARISTOTLE',
      privacyLastUpdated: 'Τελευταία ενημέρωση: 05/08/2026',
      privacyWhoWeAre: '1. ΠΟΙΟΙ ΕΙΜΑΣΤΕ',
      privacyWhoWeAreText: 'Η Villa Aristotle βρίσκεται στην Πόλη Κέρκυρας, Ελλάδα. Για οποιοδήποτε ζήτημα σχετικό με τα προσωπικά σας δεδομένα, επικοινωνήστε: villa.aristotle.cfu@gmail.com',
      privacyWhatWeCollect: '2. ΤΙ ΔΕΔΟΜΕΝΑ ΣΥΛΛΕΓΟΥΜΕ',
      privacyWhatWeCollectText: 'Όταν υποβάλλετε αίτημα κράτησης μέσω του site μας, συλλέγουμε: όνομα, email, τηλέφωνο, ημερομηνίες διαμονής, αριθμό επισκεπτών, αν ταξιδεύετε με κατοικίδιο, και προαιρετικό πεδίο ειδικών αιτημάτων. Δεν συλλέγουμε στοιχεία πληρωμής μέσω του site: η πληρωμή γίνεται εκτός site, αφού πρώτα επιβεβαιώσουμε χειροκίνητα την κράτησή σας.',
      privacyWhyWeCollect: '3. ΓΙΑΤΙ ΤΑ ΣΥΛΛΕΓΟΥΜΕ',
      privacyWhyWeCollectText: 'Αποκλειστικά για: επεξεργασία της κράτησής σας, επικοινωνία πριν/κατά τη διαμονή σας, και συμμόρφωση με φορολογικές υποχρεώσεις (ΑΑΔΕ) όπου απαιτείται.',
      privacyWhoHasAccess: '4. ΠΟΙΟΣ ΕΧΕΙ ΠΡΟΣΒΑΣΗ',
      privacyWhoHasAccessText: 'Τα δεδομένα σας δεν πωλούνται ούτε μοιράζονται με τρίτους για διαφημιστικούς σκοπούς. Μπορεί να χρησιμοποιηθούν εργαλεία διαχείρισης κρατήσεων (channel manager) αποκλειστικά για την οργάνωση της διαμονής σας.',
      privacyYourRights: '5. ΤΑ ΔΙΚΑΙΩΜΑΤΑ ΣΑΣ',
      privacyYourRightsText: 'Έχετε δικαίωμα πρόσβασης, διόρθωσης ή διαγραφής των δεδομένων σας, καθώς και δικαίωμα αντίρρησης στην επεξεργασία τους, επικοινωνώντας στο παραπάνω email. Η Villa Aristotle δεσμεύεται να προστατεύει τα προσωπικά σας δεδομένα σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (GDPR).',
      privacyCookies: '6. COOKIES & ΠΑΡΑΚΟΛΟΥΘΗΣΗ (TRACKING)',
      privacyCookiesText: 'Το site μας δεν χρησιμοποιεί κανένα εργαλείο παρακολούθησης επισκεπτών, analytics ή cookies τρίτων. Οι γραμματοσειρές για την εμφάνιση του site φορτώνονται από το Google Fonts, πράγμα που σημαίνει ότι κατά τη φόρτωση της σελίδας η Google ενδέχεται να λάβει τη διεύθυνση IP σας. Εμείς δεν έχουμε πρόσβαση σε αυτά τα δεδομένα και δεν τα χρησιμοποιούμε.',
      license: 'Άδεια Ακινήτου',
      licenseText: 'Η Villa Aristotle λειτουργεί σύμφωνα με την ελληνική νομοθεσία για τουριστικά καταλύματα και διαθέτει τις απαιτούμενες άδειες λειτουργίας σύμφωνα με το ισχύον νομικό πλαίσιο.',
    },
    // Rules
    rules: {
      title: 'Κανόνες Διαμονής',
      checkInLabel: 'Check-in',
      checkInTime: 'Από 16:00.',
      checkOutLabel: 'Check-out',
      checkOutTime: '09:30 – 10:00.',
      childrenLabel: 'Παιδιά',
      childrenText: 'Γίνονται δεκτά παιδιά όλων των ηλικιών.',
      cribsLabel: 'Βρεφικές κούνιες',
      cribsText: 'Διατίθενται δωρεάν κατόπιν αιτήματος.',
      smokingLabel: 'Κάπνισμα',
      smokingText: 'Δεν επιτρέπεται στους εσωτερικούς χώρους.',
      partiesLabel: 'Πάρτι',
      partiesText: 'Δεν επιτρέπονται πάρτι ή εκδηλώσεις.',
      quietHoursLabel: 'Ώρες ησυχίας',
      quietHoursTime: '23:00 – 08:00.',
      petsLabel: 'Κατοικίδια',
      petsText: 'Έως 1 κατοικίδιο καλοδεχούμενο, χωρίς επιπλέον χρέωση.',
      parkingLabel: 'Στάθμευση',
      parkingText: 'Δωρεάν στάθμευση για ένα όχημα. Για δεύτερο όχημα ισχύει χρέωση 5€/διανυκτέρευση.',
    },
    // Footer
    footer: {
      tagline: 'Ιδιωτική βίλα με κήπο, κοντά στην Παλαιά Πόλη της Κέρκυρας και σε παραλίες.',
      quickLinks: 'Γρήγοροι Σύνδεσμοι',
      contact: 'Επικοινωνία',
      followUs: 'Ακολουθήστε Μας',
      rights: 'Με επιφύλαξη παντός δικαιώματος.',
      privacy: 'Πολιτική Απορρήτου',
      terms: 'Όροι & Προϋποθέσεις',
      cancellation: 'Πολιτική Ακύρωσης',
      legal: 'Νομικά',
      licensedProperty: 'Αδειοδοτημένο Ακίνητο',
      licenseText: 'Η Villa Aristotle λειτουργεί σύμφωνα με την ελληνική νομοθεσία για τουριστικά καταλύματα και διαθέτει τις απαιτούμενες άδειες λειτουργίας σύμφωνα με το ισχύον νομικό πλαίσιο.',
      alsoAvailableOn: 'Επίσης διαθέσιμο σε',
      theHost: 'Η Οικοδέσποινα',
      hostText: 'Η Villa Aristotle φιλοξενείται από την Χριστίνα, η οποία φροντίζει προσωπικά για την καθαριότητα και τη φροντίδα του σπιτιού. Στόχος της είναι κάθε επισκέπτης να απολαμβάνει μια άνετη και ήρεμη διαμονή στην Κέρκυρα.',
    },
    // Common
    common: {
      viewAll: 'Προβολή Όλων',
      learnMore: 'Μάθετε Περισσότερα',
      backToTop: 'Επιστροφή στην Κορυφή',
      from: 'Από',
      currency: '€',
      comingSoon: 'Σύντομα',
      detailsComingSoon: 'Λεπτομέρειες Σύντομα',
      enquireNow: 'Ρωτήστε Τώρα',
      submitEnquiry: 'Υποβολή Αιτήματος',
      contactUs: 'Επικοινωνήστε',
      backToHome: 'Επιστροφή στην Αρχική',
      viewDetails: 'Δείτε Λεπτομέρειες',
      important: 'Σημαντικό',
    },
    // Reviews
    reviews: {
      title: 'Βαθμολογία επισκεπτών',
      subtitle: 'Δείτε τι λένε οι επισκέπτες για τη Villa Aristotle στις μεγάλες πλατφόρμες κρατήσεων.',
      reviewsLabel: 'αξιολογήσεις',
      superhost: 'Superhost (2η φορά)',
      updated: 'Ενημέρωση: Οκτώβριος 2026',
    },
    // Bedrooms
    bedrooms: {
      title: 'Υπνοδωμάτια',
      subtitle: 'Η Villa Aristotle διαθέτει 4 υπνοδωμάτια και φιλοξενεί έως 8 επισκέπτες.',
      bedroom1: 'Υπνοδωμάτιο 1',
      bedroom1Desc: 'Στο ισόγειο, με διπλό κρεβάτι, κλιματισμό με θέρμανση και Smart TV με Netflix.',
      bedroom2: 'Υπνοδωμάτιο 2',
      bedroom2Desc: 'Στο ισόγειο, με διπλό κρεβάτι, κλιματισμό με θέρμανση και Smart TV με Netflix.',
      bedroom3: 'Υπνοδωμάτιο 3',
      bedroom3Desc: 'Στο ισόγειο, με διπλό κρεβάτι, κλιματισμό με θέρμανση και Smart TV με Netflix.',
      bedroom4: 'Υπνοδωμάτιο 4',
      bedroom4Desc: 'Στο ημιυπόγειο (σουτερέν), με 2 μονά κρεβάτια και μικρό ψηλό παράθυρο. Το πιο δροσερό δωμάτιο του σπιτιού, ακόμη και το καλοκαίρι — ο δροσερός αέρας από τα κλιματιστικά του ισογείου κατεβαίνει εκεί· ανεμιστήρες οροφής και δαπέδου, χωρίς δικό του κλιματιστικό. Ανοιχτή πρόσβαση από το σαλόνι, χωρίς πόρτα.',
      needInfo: 'Χρειάζεστε Πληροφορίες Διαμονής;',
      needInfoText: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
    },
    // Villa
    villa: {
      title: 'Villa Aristotle',
      welcome: 'Καλώς ήρθατε στη Villa Aristotle',
      descPart1: 'Η Villa Aristotle είναι ιδιωτική βίλα με κήπο στην Πόλη της Κέρκυρας, 10 λεπτά από το αεροδρόμιο και το λιμάνι, κοντά στην {unesco}.',
      unescoLink: 'Παλαιά Πόλη της Κέρκυρας (Μνημείο Παγκόσμιας Κληρονομιάς UNESCO)',
      descPart2: 'Η κατοικία φιλοξενεί έως 8 επισκέπτες και διαθέτει 4 υπνοδωμάτια, πλήρως εξοπλισμένη κουζίνα, άνετους εσωτερικούς χώρους και ιδιωτικό κήπο 300 τ.μ. με εξωτερικούς χώρους χαλάρωσης.',
      descPart3: 'Η τοποθεσία της συνδυάζει εύκολη πρόσβαση στην Παλαιά Πόλη της Κέρκυρας και στις παραλίες με ήσυχο περιβάλλον για διαμονή.',
      quickEnquiry: 'Απευθείας κράτηση',
      quickEnquiryText: 'Απευθείας κράτηση. Η τιμή που βλέπετε είναι η τελική (περιλαμβάνει το τέλος διαμονής). Επιβεβαίωση από την οικοδέσποινα εντός 24 ωρών. Δεν χρεώνεστε πριν την επιβεβαίωση.',
      interiorPhoto: 'Εσωτερικός Χώρος',
      exteriorPhoto: 'Εξωτερικός Χώρος',
      viewAccommodation: 'Δείτε λεπτομέρειες διαμονής',
      discoverAvailable: 'Ανακαλύψτε τι διατίθεται',
      exploreCorfu: 'Εξερευνήστε την Κέρκυρα',
      bedrooms: '4 Υπνοδωμάτια',
      maxGuests: 'Έως 8 επισκέπτες',
    },
    // Host
    host: {
      title: 'Η Οικοδέσποινα',
      textPart1: 'Ονομάζομαι Χριστίνα και φροντίζω προσωπικά τη Villa Aristotle, ώστε κάθε επισκέπτης να απολαμβάνει μια άνετη, καθαρή και ήρεμη διαμονή στην Κέρκυρα.',
      textPart2: 'Για μένα η φιλοξενία σημαίνει προσωπική φροντίδα, προσοχή στη λεπτομέρεια και μια αυθεντική εμπειρία διαμονής.',
    },
    // Toasts (μηνύματα σφάλματος που ήταν hardcoded στα components)
    toasts: {
      fillRequired: 'Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία',
      datesUnavailable: 'Οι επιλεγμένες ημερομηνίες δεν είναι διαθέσιμες. Παρακαλώ επιλέξτε άλλες.',
      someDatesUnavailable: 'Υπάρχουν κατειλημμένες ημερομηνίες στο διάστημα που επιλέξατε. Παρακαλώ επιλέξτε άλλες.',
      bookingError: 'Σφάλμα υποβολής αιτήματος. Παρακαλώ δοκιμάστε ξανά.',
      contactError: 'Σφάλμα αποστολής μηνύματος. Παρακαλώ δοκιμάστε ξανά.',
    },
    // WhatsApp
    whatsapp: {
      message: 'Γεια σας! Θα ήθελα να ρωτήσω για τη Villa Aristotle 🌿\nΗμερομηνίες: \nΆτομα: ',
    },
  },
  de: {
    // Navigation
    nav: {
      home: 'Startseite',
      villa: 'Die Villa',
      bedrooms: 'Schlafzimmer',
      amenities: 'Ausstattung',
      gallery: 'Galerie',
      booking: 'Jetzt buchen',
      reviews: 'Bewertungen',
      location: 'Lage',
      contact: 'Kontakt',
      policies: 'Richtlinien',
      more: 'Mehr',
      host: 'Ihre Gastgeberin',
    },
    // Hero
    hero: {
      subtitle: 'Korfu, Griechenland',
      title: 'Villa Aristotle',
      tagline: 'Private Villa mit Garten – nahe der Altstadt von Korfu und den Stränden.',
      proximity: '10 Min. zu Flughafen & Hafen • In Korfu-Stadt, nahe der {unesco}',
      unescoLink: 'Altstadt (UNESCO)',
    },
    // Sections
    sections: {
      welcomeTitle: 'Willkommen in der Villa Aristotle',
      welcomeText: 'Auf der bezaubernden Insel Korfu gelegen, bietet die Villa Aristotle einen ruhigen Rückzugsort in einem der beliebtesten Reiseziele Griechenlands.',
      featuresTitle: 'Das Anwesen',
      featuresSubtitle: 'Das erwartet Sie',
      reviewsTitle: 'Erfahrungen unserer Gäste',
      reviewsSubtitle: 'Was unsere Gäste über ihren Aufenthalt sagen',
      locationTitle: 'Beste Lage',
      locationSubtitle: 'Korfu, Griechenland',
      ctaTitle: 'Fragen Sie Ihren Aufenthalt an',
      ctaText: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
    },
    // Features
    features: {
      spaciousVilla: 'Großzügige Villa',
      spaciousVillaDesc: 'Komfortable Unterkunft',
      bedrooms: 'Mehrere Schlafzimmer',
      bedroomsDesc: 'Platz für bis zu 8 Gäste',
      location: 'Lage auf Korfu',
      locationDesc: 'Griechisches Inselambiente',
    },
    // Booking Widget & Page
    booking: {
      checkIn: 'Anreise',
      checkOut: 'Abreise',
      guests: 'Gäste',
      guest: 'Gast',
      selectDate: 'Datum wählen',
      checkAvailability: 'Verfügbarkeit prüfen',
      requestBooking: 'Buchen',
      perNight: 'pro Nacht',
      nights: 'Nächte',
      night: 'Nacht',
      petQuestion: 'Reisen Sie mit einem Haustier?',
      petNote: 'Wir heißen bis zu 1 Haustier willkommen, ohne Aufpreis.',
      petFee: 'Haustier',
      yes: 'Ja',
      no: 'Nein',
      petRequired: 'Bitte wählen Sie aus, ob Sie ein Haustier mitbringen (Ja / Nein).',
      vehicleQuestion: 'Zweites Fahrzeug (5 €/Nacht)',
      vehicleFee: 'Zweites Fahrzeug',
      total: 'Endpreis',
      envFee: 'Klimakrisen-Resilienzgebühr (CRF)',
      envFeeShort: 'CRF',
      infoLabel: 'Weitere Informationen',
      envFeeInfo: 'Staatliche Abgabe zur Bewältigung der Klimakrise. 15 €/Nacht von April bis Oktober, 4 €/Nacht von November bis März. Gesetzlich vorgeschrieben.',
      vehicleInfo: 'Der Parkplatz für ein Fahrzeug ist kostenfrei. Für ein zweites Fahrzeug berechnen wir 5 €/Nacht.',
      priceInfo: 'Der Preis gilt für die Übernachtung pro Nacht. Die CRF und eventuelle Zusatzleistungen werden separat ausgewiesen. Für Übernachtungen vom 26.10.–31.10.2026, 16.11.–10.12.2026 und 08.01.–28.02.2027: −15 % für 1–2 Personen, −10 % für 3–4 Personen.',
      lengthDiscount: 'Rabatt für längere Aufenthalte',
      scarcityOne: 'Nur noch 1 freier Tag im',
      scarcityMany: 'Nur noch {n} freie Tage im',
      discount: 'Rabatt',
      discountCode: 'Rabattcode',
      apply: 'Einlösen',
      applied: 'Eingelöst!',
      addOns: 'Zusatzleistungen & Erlebnisse',
      yourDetails: 'Ihre Daten',
      fullName: 'Vollständiger Name',
      email: 'E-Mail',
      phone: 'Telefon',
      message: 'Besondere Wünsche (optional)',
      submit: 'Buchen',
      submitting: 'Wird gesendet …',
      success: 'Ihre Buchungsanfrage wurde erfolgreich gesendet!',
      successMessage: 'Wir melden uns innerhalb von 24 Stunden bei Ihnen, um Ihre Buchung zu bestätigen.',
      enquiryNotice: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      airportNotice: 'Hinweis: Der Flughafen Korfu ist vom 20.–28. Januar 2027 wegen Bauarbeiten geschlossen. Bitte berücksichtigen Sie dies bei der Planung Ihrer Anreise.',
      minStayError: 'Der Mindestaufenthalt für diesen Zeitraum beträgt {n} Nächte.',
      sameDayError: 'Buchungen für denselben Tag sind nur bis 10:00 Uhr (nach griechischer Zeit) möglich. Bitte wählen Sie ein späteres Anreisedatum.',
      maxGuests: 'Maximal 8 Gäste (9 mit Schlafsofa auf Anfrage)',
      bookYourStay: 'Buchen Sie Ihren Aufenthalt',
      bookingSubtitle: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      selectYourDates: 'Wählen Sie Ihren Zeitraum',
      priceSummary: 'Preisübersicht',
      selectDatesToSeePricing: 'Wählen Sie einen Zeitraum, um die Preise zu sehen',
      season: 'Saison',
      yourBookingEnquiry: 'Ihre Buchungsanfrage',
      dates: 'Zeitraum',
      estimatedTotal: 'Endpreis',
      subjectToConfirmation: 'Bestätigung innerhalb von 24 Stunden',
      backToHome: 'Zurück zur Startseite',
      noChargeYet: 'Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      noChargeYetLong: 'Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      enterCode: 'Code eingeben',
      discountAppliedText: 'Rabatt eingelöst',
      discountCodeLabel: 'Rabattcode (optional)',
      discountInvalid: 'Dieser Rabattcode ist nicht gültig. Bitte korrigieren Sie ihn oder lassen Sie das Feld leer.',
      priceBeforeDiscount: 'Preis vor Rabatt',
      discountCodeLine: 'Code {code}',
      enquireNow: 'Jetzt buchen',
      checkAvailabilityShort: 'Verfügbarkeit prüfen',
      wonNotChargedYet: 'Es erfolgt noch keine Zahlung',
      specialRequestsPlaceholder: 'Besondere Wünsche, Ankunftszeit usw.',
    },
    // Contact
    contact: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Wir freuen uns, von Ihnen zu hören',
      name: 'Ihr Name',
      email: 'E-Mail-Adresse',
      phone: 'Telefonnummer',
      subject: 'Betreff',
      message: 'Ihre Nachricht',
      send: 'Nachricht senden',
      sending: 'Wird gesendet …',
      address: 'Adresse',
      success: 'Ihre Nachricht wurde erfolgreich gesendet!',
      addressStreet: 'Toni Spiteri 11',
      addressCity: 'Korfu-Stadt 49100',
      addressCountry: 'Griechenland',
      responseTime: 'Wir antworten in der Regel innerhalb von 24 Stunden.',
      contactInfo: 'Kontaktdaten',
      responseTimeLabel: 'Antwortzeit',
      responseTimeText: 'Wir antworten in der Regel innerhalb von 24 Stunden.',
      thankYou: 'Vielen Dank für Ihre Nachricht. Wir antworten Ihnen innerhalb von 24 Stunden.',
    },
    // Gallery
    gallery: {
      title: 'Fotogalerie',
      subtitle: 'Sehen Sie Fotos der Villa Aristotle sowie ihrer Innen- und Außenbereiche.',
      exterior: 'Außenansicht',
      garden: 'Garten',
      terrace: 'Terrasse',
      livingRoom: 'Wohnzimmer',
      kitchen: 'Küche',
      bedroom: 'Schlafzimmer',
    },
    // Amenities
    amenities: {
      title: 'Ausstattung der Villa Aristotle',
      internetTitle: 'Internet & Unterhaltung',
      wifi: 'Kostenfreies WLAN im gesamten Haus',
      netflix: 'Netflix',
      smartTv: 'Smart-TV',
      tvBedroom: 'TV in jedem Schlafzimmer',
      workDesk: 'Schreibtisch',
      accommodationTitle: 'Unterkunft',
      fourBedrooms: '4 Schlafzimmer',
      eightGuests: 'Bis zu 8 Gäste',
      linens: 'Bettwäsche',
      towels: 'Handtücher',
      travelCots: '2 Reisebetten für Babys',
      kitchenTitle: 'Küche',
      fullyEquipped: 'Voll ausgestattete Küche',
      fridge: 'Kühlschrank',
      oven: 'Backofen',
      stove: 'Herd',
      coffeeMaker: 'Nespresso-Maschine (Kapseln), Filterkaffee und griechischer Kaffee',
      kettle: 'Wasserkocher',
      cookware: 'Kochgeschirr',
      bathroomTitle: 'Badezimmer',
      privateBathrooms: '2 Bäder (eines mit Badewanne, eines mit Dusche)',
      showerBath: 'Dusche oder Badewanne',
      hairDryer: 'Haartrockner',
      toiletries: 'Pflegeprodukte',
      outdoorTitle: 'Außenbereiche',
      privateGarden: 'Privater Garten (300 m²)',
      terrace: 'Terrasse / Balkon',
      outdoorSeating: 'Sitzbereich im Freien',
      bbq: 'Grill',
      practicalTitle: 'Praktische Ausstattung',
      parking: 'Kostenfreier Parkplatz für ein Fahrzeug (zweites Fahrzeug 5 €/Nacht)',
      safe: 'Safe',
      washingMachine: 'Waschmaschine',
      dryer: 'Wäschetrockner',
      airConditioning: 'Klimaanlage mit Heizfunktion in den 3 Schlafzimmern im Erdgeschoss; Heizkörper im ganzen Haus',
      heating: 'Heizung',
      gym: 'Kleiner Fitnessraum (Laufband, Hanteln, Widerstandsbänder)',
      keyBox: 'Schlüsselsafe – selbstständige Anreise zu jeder Uhrzeit',
      pet: 'Haustier willkommen (max. 1, kostenlos)',
    },
    // Location
    location: {
      title: 'Korfu, Griechenland',
      subtitle: 'Die Villa Aristotle liegt auf der schönen Insel Korfu, einem der bezauberndsten Reiseziele im Ionischen Meer. Ca. 10 Minuten mit dem Auto zum Flughafen (CFU) und zum Hafen, ca. 10 Minuten mit dem Auto zur Altstadt (UNESCO).',
      byAir: 'Mit dem Flugzeug',
      byAirDesc: 'Flughafen Korfu (CFU)',
      byAirText: 'Direktflüge aus vielen europäischen Städten.',
      byCar: 'Mit dem Auto',
      byCarDesc: 'Mietwagen empfohlen',
      byCarText: 'Für Ausflüge auf Korfu empfehlen wir einen Mietwagen. Gerne unterstützen wir Sie bei der Vermittlung an örtliche Anbieter.',
      aboutCorfu: 'Entdecken Sie Korfu',
      aboutCorfuText: 'Korfu (Kerkyra) ist eine der schönsten griechischen Inseln – bekannt für ihre üppig grünen Landschaften, die venezianische Architektur und die beeindruckende Küste.',
      gettingHere: 'Anreise',
      aboutTheIsland: 'Über die Insel',
      discoverCorfuText: 'Korfu (Kerkyra) ist eine der schönsten griechischen Inseln – bekannt für ihre üppig grünen Landschaften, die venezianische Architektur und die beeindruckende Küste. Die Insel verbindet Naturschönheit, reiche Geschichte und lebendige Kultur auf ideale Weise.',
      oldTownText: 'Die zum UNESCO-Welterbe zählende Altstadt beeindruckt mit mächtigen Festungen, engen Gassen und italienisch geprägter Architektur. Die Insel ist bekannt für ihre ausgezeichnete regionale Küche, das kristallklare Wasser und die herzliche Gastfreundschaft.',
      openInMaps: 'In Google Maps öffnen',
    },
    // Policies
    policies: {
      title: 'Buchungsrichtlinien',
      subtitle: 'Bitte lesen Sie unsere Richtlinien, bevor Sie buchen.',
      terms: 'Allgemeine Geschäftsbedingungen',
      bookingProcess: 'Buchungsablauf',
      bookingProcessText: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      paymentTerms: 'Zahlungsbedingungen',
      paymentTermsText: 'Die Zahlungsanweisungen erhalten Sie mit der Buchungsbestätigung.',
      checkInOut: 'Check-in / Check-out',
      checkIn: 'Check-in: 16:00 Uhr',
      checkOut: 'Check-out: bis 10:00 Uhr',
      cancellation: 'Stornierungsbedingungen',
      cancellationText: 'Kostenlose Stornierung bis 60 Tage vor Anreise (volle Rückerstattung). Bei Stornierung innerhalb von 60 Tagen vor Anreise wird der Betrag nicht erstattet.',
      modifications: 'Änderungen',
      modificationsText: 'Terminänderungen sind nach Verfügbarkeit möglich. Bitte kontaktieren Sie uns so früh wie möglich, wenn Sie Ihre Buchung ändern möchten.',
      damageDeposit: 'Kaution',
      damageDepositText: 'Es wird eine Kaution von 500 € erhoben, die innerhalb von 7 Tagen nach der Abreise zurückerstattet wird.',
      houseRules: 'Hausordnung',
      houseRulesText: 'Die konkrete Hausordnung erhalten Sie mit der Buchungsbestätigung. Grundsätzlich gilt: Rauchverbot im Innenbereich und Einhaltung der Ruhezeiten.',
      noSmoking: 'Rauchen im Innenbereich nicht gestattet',
      noParties: 'Keine Partys ohne vorherige Absprache',
      quietHours: 'Ruhezeiten: 23:00 – 08:00 Uhr',
      maxOccupancy: 'Maximale Belegung: 8 Gäste (9 mit Schlafsofa auf Anfrage)',
      privacy: 'DATENSCHUTZERKLÄRUNG — VILLA ARISTOTLE',
      privacyLastUpdated: 'Zuletzt aktualisiert: 05.08.2026',
      privacyWhoWeAre: '1. WER WIR SIND',
      privacyWhoWeAreText: 'Die Villa Aristotle befindet sich in Korfu-Stadt, Griechenland. Bei allen Fragen zu Ihren personenbezogenen Daten wenden Sie sich bitte an: villa.aristotle.cfu@gmail.com',
      privacyWhatWeCollect: '2. WELCHE DATEN WIR ERHEBEN',
      privacyWhatWeCollectText: 'Wenn Sie über unsere Website eine Buchungsanfrage stellen, erheben wir: Name, E-Mail-Adresse, Telefonnummer, Reisedaten, Anzahl der Gäste, die Angabe, ob Sie mit einem Haustier reisen, sowie ein optionales Feld für besondere Wünsche. Zahlungsdaten erheben wir nicht über die Website: Die Zahlung erfolgt außerhalb der Website, nachdem wir Ihre Buchung persönlich bestätigt haben.',
      privacyWhyWeCollect: '3. WARUM WIR SIE ERHEBEN',
      privacyWhyWeCollectText: 'Ausschließlich für: die Bearbeitung Ihrer Buchung, die Kommunikation vor und während Ihres Aufenthalts sowie die Erfüllung steuerlicher Pflichten (AADE), soweit erforderlich.',
      privacyWhoHasAccess: '4. WER ZUGRIFF HAT',
      privacyWhoHasAccessText: 'Ihre Daten werden nicht verkauft und nicht zu Werbezwecken an Dritte weitergegeben. Buchungsverwaltungssysteme (Channel Manager) werden ausschließlich zur Organisation Ihres Aufenthalts eingesetzt.',
      privacyYourRights: '5. IHRE RECHTE',
      privacyYourRightsText: 'Sie haben das Recht auf Auskunft, Berichtigung und Löschung Ihrer Daten sowie das Recht, der Verarbeitung zu widersprechen – wenden Sie sich dazu an die oben genannte E-Mail-Adresse. Die Villa Aristotle verpflichtet sich, Ihre personenbezogenen Daten gemäß der Datenschutz-Grundverordnung (DSGVO) zu schützen.',
      privacyCookies: '6. COOKIES & TRACKING',
      privacyCookiesText: 'Unsere Website verwendet keinerlei Tracking-Tools, Analysedienste oder Cookies von Drittanbietern. Die Schriftarten für die Darstellung der Website werden von Google Fonts geladen; dabei kann Google beim Laden einer Seite Ihre IP-Adresse erhalten. Wir selbst haben keinen Zugriff auf diese Daten und verwenden sie nicht.',
      license: 'Genehmigung der Unterkunft',
      licenseText: 'Die Villa Aristotle wird gemäß den griechischen Vorschriften für touristische Unterkünfte betrieben und verfügt über die nach griechischem Recht erforderlichen Betriebsgenehmigungen.',
    },
    // Rules
    rules: {
      title: 'Hausordnung',
      checkInLabel: 'Check-in',
      checkInTime: 'Ab 16:00 Uhr.',
      checkOutLabel: 'Check-out',
      checkOutTime: '09:30 – 10:00 Uhr.',
      childrenLabel: 'Kinder',
      childrenText: 'Kinder jeden Alters sind willkommen.',
      cribsLabel: 'Babybetten',
      cribsText: 'Auf Anfrage kostenfrei verfügbar.',
      smokingLabel: 'Rauchen',
      smokingText: 'Im Innenbereich nicht gestattet.',
      partiesLabel: 'Partys',
      partiesText: 'Partys und Veranstaltungen sind nicht gestattet.',
      quietHoursLabel: 'Ruhezeiten',
      quietHoursTime: '23:00 – 08:00 Uhr.',
      petsLabel: 'Haustiere',
      petsText: 'Bis zu 1 Haustier willkommen, ohne Aufpreis.',
      parkingLabel: 'Parken',
      parkingText: 'Kostenfreier Parkplatz für ein Fahrzeug. Für ein zweites Fahrzeug berechnen wir 5 €/Nacht.',
    },
    // Footer
    footer: {
      tagline: 'Private Villa mit Garten – nahe der Altstadt von Korfu und den Stränden.',
      quickLinks: 'Schnellzugriff',
      contact: 'Kontakt',
      followUs: 'Folgen Sie uns',
      rights: 'Alle Rechte vorbehalten.',
      privacy: 'Datenschutzerklärung',
      terms: 'Allgemeine Geschäftsbedingungen',
      cancellation: 'Stornierungsbedingungen',
      legal: 'Rechtliches',
      licensedProperty: 'Genehmigte Unterkunft',
      licenseText: 'Die Villa Aristotle wird gemäß den griechischen Vorschriften für touristische Unterkünfte betrieben und verfügt über die nach griechischem Recht erforderlichen Betriebsgenehmigungen.',
      alsoAvailableOn: 'Auch verfügbar auf',
      theHost: 'Ihre Gastgeberin',
      hostText: 'Die Villa Aristotle wird von Christina geführt, die sich persönlich um das Haus und seine Vorbereitung für jeden Gast kümmert – für einen komfortablen und erholsamen Aufenthalt auf Korfu.',
    },
    // Common
    common: {
      viewAll: 'Alle ansehen',
      learnMore: 'Mehr erfahren',
      backToTop: 'Nach oben',
      from: 'Ab',
      currency: '€',
      comingSoon: 'Demnächst',
      detailsComingSoon: 'Details folgen in Kürze',
      enquireNow: 'Jetzt anfragen',
      submitEnquiry: 'Anfrage senden',
      contactUs: 'Kontakt aufnehmen',
      backToHome: 'Zurück zur Startseite',
      viewDetails: 'Details ansehen',
      important: 'Wichtig',
    },
    // Reviews
    reviews: {
      title: 'Gästebewertungen',
      subtitle: 'Sehen Sie, was Gäste auf den großen Buchungsplattformen über die Villa Aristotle sagen.',
      reviewsLabel: 'Bewertungen',
      superhost: 'Superhost (zum 2. Mal)',
      updated: 'Stand: Oktober 2026',
    },
    // Bedrooms
    bedrooms: {
      title: 'Schlafzimmer',
      subtitle: 'Die Villa Aristotle verfügt über 4 Schlafzimmer und bietet Platz für bis zu 8 Gäste.',
      bedroom1: 'Schlafzimmer 1',
      bedroom1Desc: 'Im Erdgeschoss, mit Doppelbett, Klimaanlage mit Heizfunktion und Smart-TV mit Netflix.',
      bedroom2: 'Schlafzimmer 2',
      bedroom2Desc: 'Im Erdgeschoss, mit Doppelbett, Klimaanlage mit Heizfunktion und Smart-TV mit Netflix.',
      bedroom3: 'Schlafzimmer 3',
      bedroom3Desc: 'Im Erdgeschoss, mit Doppelbett, Klimaanlage mit Heizfunktion und Smart-TV mit Netflix.',
      bedroom4: 'Schlafzimmer 4',
      bedroom4Desc: 'Im Souterrain, mit 2 Einzelbetten und einem kleinen, hoch liegenden Fenster. Das kühlste Zimmer des Hauses, auch im Sommer – die kühle Luft der Klimaanlagen im Erdgeschoss zieht hinunter; Decken- und Standventilatoren, keine eigene Klimaanlage. Offener Zugang vom Wohnzimmer, ohne Tür.',
      needInfo: 'Brauchen Sie Informationen zur Unterkunft?',
      needInfoText: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
    },
    // Villa
    villa: {
      title: 'Villa Aristotle',
      welcome: 'Willkommen in der Villa Aristotle',
      descPart1: 'Die Villa Aristotle ist eine private Villa mit Garten in Korfu-Stadt, 10 Minuten vom Flughafen und Hafen entfernt, nahe der {unesco}.',
      unescoLink: 'Altstadt von Korfu (UNESCO-Weltkulturerbe)',
      descPart2: 'Das Haus bietet Platz für bis zu 8 Gäste und verfügt über 4 Schlafzimmer, eine voll ausgestattete Küche, komfortable Innenräume und einen 300 m² großen privaten Garten mit Ruhebereichen im Freien.',
      descPart3: 'Die Lage verbindet die gute Erreichbarkeit der Altstadt von Korfu und der Strände mit einer ruhigen Umgebung für Ihren Aufenthalt.',
      quickEnquiry: 'Direktbuchung',
      quickEnquiryText: 'Direktbuchung. Der angezeigte Preis ist der Endpreis (inklusive CRF). Bestätigung durch die Gastgeberin innerhalb von 24 Stunden. Vor der Bestätigung wird nichts berechnet.',
      interiorPhoto: 'Innenbereich',
      exteriorPhoto: 'Außenbereich',
      viewAccommodation: 'Details zur Unterkunft ansehen',
      discoverAvailable: 'Entdecken Sie die Ausstattung',
      exploreCorfu: 'Korfu, Griechenland entdecken',
      bedrooms: '4 Schlafzimmer',
      maxGuests: 'Bis zu 8 Gäste',
    },
    // Host
    host: {
      title: 'Ihre Gastgeberin',
      textPart1: 'Mein Name ist Christina und ich kümmere mich persönlich um die Villa Aristotle, damit jeder Gast einen komfortablen, gepflegten und erholsamen Aufenthalt auf Korfu genießen kann.',
      textPart2: 'Gastfreundschaft bedeutet für mich persönliche Fürsorge, Liebe zum Detail und ein authentisches Wohnerlebnis.',
    },
    // Toasts (μηνύματα σφάλματος που ήταν hardcoded στα components)
    toasts: {
      fillRequired: 'Bitte füllen Sie alle Pflichtfelder aus',
      datesUnavailable: 'Der gewählte Zeitraum ist nicht verfügbar. Bitte wählen Sie andere Daten.',
      someDatesUnavailable: 'In Ihrem gewählten Zeitraum sind einzelne Tage bereits belegt. Bitte wählen Sie andere Daten.',
      bookingError: 'Beim Senden Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      contactError: 'Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    },
    // WhatsApp
    whatsapp: {
      message: 'Guten Tag! Ich interessiere mich für die Villa Aristotle 🌿\nZeitraum: \nPersonen: ',
    },
  },
  it: {
    // Navigation
    nav: {
      home: 'Home',
      villa: 'La Villa',
      bedrooms: 'Camere',
      amenities: 'Servizi',
      gallery: 'Galleria',
      booking: 'Prenota ora',
      reviews: 'Recensioni',
      location: 'Posizione',
      contact: 'Contatti',
      policies: 'Condizioni',
      more: 'Altro',
      host: 'La padrona di casa',
    },
    // Hero
    hero: {
      subtitle: 'Corfù, Grecia',
      title: 'Villa Aristotle',
      tagline: 'Villa privata con giardino, vicino al Centro Storico di Corfù e alle spiagge.',
      proximity: '10 min da aeroporto e porto • A Corfù città, accanto al {unesco}',
      unescoLink: 'Centro Storico di Corfù',
    },
    // Sections
    sections: {
      welcomeTitle: 'Benvenuti a Villa Aristotle',
      welcomeText: 'Situata sull\'incantevole isola di Corfù, Villa Aristotle offre un rifugio tranquillo in una delle mete più amate della Grecia.',
      featuresTitle: 'La Proprietà',
      featuresSubtitle: 'Cosa La aspetta',
      reviewsTitle: 'Le esperienze dei nostri ospiti',
      reviewsSubtitle: 'Cosa dicono i nostri ospiti del loro soggiorno',
      locationTitle: 'Posizione privilegiata',
      locationSubtitle: 'Corfù, Grecia',
      ctaTitle: 'Richieda informazioni sul Suo soggiorno',
      ctaText: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
    },
    // Features
    features: {
      spaciousVilla: 'Villa spaziosa',
      spaciousVillaDesc: 'Sistemazione confortevole',
      bedrooms: 'Più camere da letto',
      bedroomsDesc: 'Fino a 8 ospiti',
      location: 'Posizione a Corfù',
      locationDesc: 'Ambiente isolano greco',
    },
    // Booking Widget & Page
    booking: {
      checkIn: 'Arrivo',
      checkOut: 'Partenza',
      guests: 'Ospiti',
      guest: 'Ospite',
      selectDate: 'Scelga la data',
      checkAvailability: 'Verifica disponibilità',
      requestBooking: 'Prenota',
      perNight: 'a notte',
      nights: 'notti',
      night: 'notte',
      petQuestion: 'Viaggia con un animale domestico?',
      petNote: 'Accogliamo fino a 1 animale domestico, senza costi aggiuntivi.',
      petFee: 'Animale domestico',
      yes: 'Sì',
      no: 'No',
      petRequired: 'Indichi se viaggia con un animale domestico (Sì / No).',
      vehicleQuestion: 'Secondo veicolo (5 €/notte)',
      vehicleFee: 'Secondo veicolo',
      total: 'Prezzo finale',
      envFee: 'Tassa di resilienza climatica (CRF)',
      envFeeShort: 'CRF',
      infoLabel: 'Maggiori informazioni',
      envFeeInfo: 'Tassa statale per la resilienza alla crisi climatica. 15 €/notte da aprile a ottobre, 4 €/notte da novembre a marzo. Obbligatoria per legge.',
      vehicleInfo: 'Il parcheggio per un veicolo è gratuito. Per un secondo veicolo il costo è di 5 €/notte.',
      priceInfo: 'Il prezzo si riferisce al pernottamento per notte. La CRF ed eventuali servizi aggiuntivi sono indicati separatamente. Per i pernottamenti dal 26/10 al 31/10/2026, dal 16/11 al 10/12/2026 e dall\'8/01 al 28/02/2027: −15% per 1–2 persone, −10% per 3–4 persone.',
      lengthDiscount: 'Sconto per soggiorni lunghi',
      scarcityOne: 'Solo 1 giorno ancora disponibile nel mese di',
      scarcityMany: 'Solo {n} giorni ancora disponibili nel mese di',
      discount: 'Sconto',
      discountCode: 'Codice sconto',
      apply: 'Applica',
      applied: 'Applicato!',
      addOns: 'Servizi ed esperienze extra',
      yourDetails: 'I Suoi dati',
      fullName: 'Nome e cognome',
      email: 'E-mail',
      phone: 'Telefono',
      message: 'Richieste particolari (facoltativo)',
      submit: 'Prenota',
      submitting: 'Invio in corso…',
      success: 'Richiesta di prenotazione inviata con successo!',
      successMessage: 'La contatteremo entro 24 ore per confermare la Sua prenotazione.',
      enquiryNotice: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      airportNotice: 'Nota: l\'aeroporto di Corfù resterà chiuso dal 20 al 28 gennaio 2027 per lavori. La preghiamo di tenerne conto nella pianificazione del Suo arrivo.',
      minStayError: 'Il soggiorno minimo per queste date è di {n} notti.',
      sameDayError: 'Le prenotazioni per lo stesso giorno sono possibili solo fino alle 10:00 (ora greca). La preghiamo di scegliere una data di arrivo successiva.',
      maxGuests: 'Massimo 8 ospiti (9 con divano letto su richiesta)',
      bookYourStay: 'Prenoti il Suo soggiorno',
      bookingSubtitle: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      selectYourDates: 'Scelga le date',
      priceSummary: 'Riepilogo prezzi',
      selectDatesToSeePricing: 'Selezioni le date per vedere i prezzi',
      season: 'Stagione',
      yourBookingEnquiry: 'La Sua richiesta di prenotazione',
      dates: 'Date',
      estimatedTotal: 'Prezzo finale',
      subjectToConfirmation: 'conferma entro 24 ore',
      backToHome: 'Torna alla home',
      noChargeYet: 'Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      noChargeYetLong: 'Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      enterCode: 'Inserisca il codice',
      discountAppliedText: 'sconto applicato',
      discountCodeLabel: 'Codice sconto (facoltativo)',
      discountInvalid: 'Il codice sconto non è valido. La preghiamo di correggerlo o di lasciare il campo vuoto.',
      priceBeforeDiscount: 'Prezzo prima dello sconto',
      discountCodeLine: 'Codice {code}',
      enquireNow: 'Prenota ora',
      checkAvailabilityShort: 'Verifica disponibilità',
      wonNotChargedYet: 'Non Le verrà ancora addebitato nulla',
      specialRequestsPlaceholder: 'Richieste particolari, orario di arrivo, ecc.',
    },
    // Contact
    contact: {
      title: 'Ci contatti',
      subtitle: 'Saremo felici di sentirLa',
      name: 'Il Suo nome',
      email: 'Indirizzo e-mail',
      phone: 'Numero di telefono',
      subject: 'Oggetto',
      message: 'Il Suo messaggio',
      send: 'Invia messaggio',
      sending: 'Invio in corso…',
      address: 'Indirizzo',
      success: 'Messaggio inviato con successo!',
      addressStreet: 'Toni Spiteri 11',
      addressCity: 'Corfù città 49100',
      addressCountry: 'Grecia',
      responseTime: 'Rispondiamo generalmente entro 24 ore.',
      contactInfo: 'Informazioni di contatto',
      responseTimeLabel: 'Tempo di risposta',
      responseTimeText: 'Rispondiamo generalmente entro 24 ore.',
      thankYou: 'Grazie per averci contattato. Risponderemo al Suo messaggio entro 24 ore.',
    },
    // Gallery
    gallery: {
      title: 'Galleria fotografica',
      subtitle: 'Guardi le foto di Villa Aristotle e dei suoi spazi interni ed esterni.',
      exterior: 'Esterno',
      garden: 'Giardino',
      terrace: 'Terrazza',
      livingRoom: 'Soggiorno',
      kitchen: 'Cucina',
      bedroom: 'Camera da letto',
    },
    // Amenities
    amenities: {
      title: 'Servizi di Villa Aristotle',
      internetTitle: 'Internet e intrattenimento',
      wifi: 'Wi-Fi gratuito in tutta la casa',
      netflix: 'Netflix',
      smartTv: 'Smart TV',
      tvBedroom: 'TV in ogni camera',
      workDesk: 'Scrivania',
      accommodationTitle: 'Sistemazione',
      fourBedrooms: '4 camere da letto',
      eightGuests: 'Fino a 8 ospiti',
      linens: 'Biancheria da letto',
      towels: 'Asciugamani',
      travelCots: '2 lettini da viaggio per neonati',
      kitchenTitle: 'Cucina',
      fullyEquipped: 'Cucina completamente attrezzata',
      fridge: 'Frigorifero',
      oven: 'Forno',
      stove: 'Piano cottura',
      coffeeMaker: 'Macchina Nespresso (capsule), caffè filtro e caffè greco',
      kettle: 'Bollitore',
      cookware: 'Pentole e utensili',
      bathroomTitle: 'Bagno',
      privateBathrooms: '2 bagni (uno con vasca, uno con doccia)',
      showerBath: 'Doccia o vasca',
      hairDryer: 'Asciugacapelli',
      toiletries: 'Prodotti da bagno',
      outdoorTitle: 'Spazi esterni',
      privateGarden: 'Giardino privato (300 m²)',
      terrace: 'Terrazza / balcone',
      outdoorSeating: 'Zona relax all\'aperto',
      bbq: 'Barbecue',
      practicalTitle: 'Servizi pratici',
      parking: 'Parcheggio gratuito per un veicolo (secondo veicolo 5 €/notte)',
      safe: 'Cassaforte',
      washingMachine: 'Lavatrice',
      dryer: 'Asciugatrice',
      airConditioning: 'Aria condizionata con riscaldamento nelle 3 camere al piano terra; termosifoni in tutta la casa',
      heating: 'Riscaldamento',
      gym: 'Piccola palestra (tapis roulant, manubri, elastici)',
      keyBox: 'Cassetta portachiavi – arrivo autonomo a qualsiasi ora',
      pet: 'Animale domestico ammesso (max 1, gratuito)',
    },
    // Location
    location: {
      title: 'Corfù, Grecia',
      subtitle: 'Villa Aristotle si trova sulla splendida isola di Corfù, una delle mete più incantevoli del Mar Ionio. 10 minuti in auto dall\'aeroporto (CFU) e dal porto, 10 minuti in auto dal centro storico (UNESCO).',
      byAir: 'In aereo',
      byAirDesc: 'Aeroporto Internazionale di Corfù (CFU)',
      byAirText: 'Voli diretti dalle principali città europee.',
      byCar: 'In auto',
      byCarDesc: 'Consigliato il noleggio',
      byCarText: 'Per esplorare Corfù consigliamo di noleggiare un\'auto. Possiamo aiutarLa nella prenotazione presso fornitori locali.',
      aboutCorfu: 'Scopra Corfù',
      aboutCorfuText: 'Corfù (Kerkyra) è una delle isole greche più belle, nota per i suoi paesaggi verdeggianti, l\'architettura veneziana e la costa spettacolare.',
      gettingHere: 'Come arrivare',
      aboutTheIsland: 'Sull\'isola',
      discoverCorfuText: 'Corfù (Kerkyra) è una delle isole greche più belle, nota per i suoi paesaggi verdeggianti, l\'architettura veneziana e la costa spettacolare. L\'isola unisce in modo perfetto bellezza naturale, storia ricca e cultura viva.',
      oldTownText: 'Il Centro Storico, Patrimonio Mondiale UNESCO, custodisce imponenti fortezze, vicoli stretti e un\'architettura di influenza italiana. L\'isola è nota per l\'ottima cucina locale, le acque cristalline e la calorosa ospitalità.',
      openInMaps: 'Apri in Google Maps',
    },
    // Policies
    policies: {
      title: 'Condizioni di prenotazione',
      subtitle: 'La preghiamo di leggere le nostre condizioni prima di prenotare.',
      terms: 'Termini e condizioni',
      bookingProcess: 'Procedura di prenotazione',
      bookingProcessText: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      paymentTerms: 'Condizioni di pagamento',
      paymentTermsText: 'Le istruzioni di pagamento Le vengono inviate con la conferma della prenotazione.',
      checkInOut: 'Check-in / Check-out',
      checkIn: 'Check-in: 16:00',
      checkOut: 'Check-out: entro le 10:00',
      cancellation: 'Politica di cancellazione',
      cancellationText: 'Cancellazione gratuita fino a 60 giorni prima dell\'arrivo (rimborso completo). In caso di cancellazione nei 60 giorni precedenti l\'arrivo, l\'importo non viene rimborsato.',
      modifications: 'Modifiche',
      modificationsText: 'Le modifiche di data sono soggette a disponibilità. La preghiamo di contattarci il prima possibile se desidera modificare la Sua prenotazione.',
      damageDeposit: 'Cauzione',
      damageDepositText: 'È richiesta una cauzione di 500 €, che viene restituita entro 7 giorni dalla partenza.',
      houseRules: 'Regolamento della casa',
      houseRulesText: 'Il regolamento specifico Le sarà fornito con la conferma di prenotazione. In generale: vietato fumare all\'interno e rispetto delle ore di silenzio.',
      noSmoking: 'Vietato fumare all\'interno',
      noParties: 'Nessuna festa senza previo accordo',
      quietHours: 'Ore di silenzio: 23:00 – 08:00',
      maxOccupancy: 'Capienza massima: 8 ospiti (9 con divano letto su richiesta)',
      privacy: 'INFORMATIVA SULLA PRIVACY — VILLA ARISTOTLE',
      privacyLastUpdated: 'Ultimo aggiornamento: 05/08/2026',
      privacyWhoWeAre: '1. CHI SIAMO',
      privacyWhoWeAreText: 'Villa Aristotle si trova a Corfù città, in Grecia. Per qualsiasi questione relativa ai Suoi dati personali scriva a: villa.aristotle.cfu@gmail.com',
      privacyWhatWeCollect: '2. QUALI DATI RACCOGLIAMO',
      privacyWhatWeCollectText: 'Quando invia una richiesta di prenotazione tramite il nostro sito raccogliamo: nome, e-mail, telefono, date del soggiorno, numero di ospiti, l\'eventuale presenza di un animale domestico e un campo facoltativo per richieste particolari. Non raccogliamo dati di pagamento tramite il sito: il pagamento avviene al di fuori del sito, dopo che abbiamo confermato personalmente la Sua prenotazione.',
      privacyWhyWeCollect: '3. PERCHÉ LI RACCOGLIAMO',
      privacyWhyWeCollectText: 'Esclusivamente per: la gestione della Sua prenotazione, la comunicazione prima e durante il soggiorno e l\'adempimento degli obblighi fiscali (AADE), ove richiesto.',
      privacyWhoHasAccess: '4. CHI VI HA ACCESSO',
      privacyWhoHasAccessText: 'I Suoi dati non vengono venduti né condivisi con terzi a fini pubblicitari. Gli strumenti di gestione delle prenotazioni (channel manager) sono utilizzati esclusivamente per organizzare il Suo soggiorno.',
      privacyYourRights: '5. I SUOI DIRITTI',
      privacyYourRightsText: 'Ha il diritto di accedere, rettificare o cancellare i Suoi dati, nonché di opporsi al loro trattamento, scrivendo all\'indirizzo e-mail sopra indicato. Villa Aristotle si impegna a proteggere i Suoi dati personali in conformità al Regolamento Generale sulla Protezione dei Dati (GDPR).',
      privacyCookies: '6. COOKIE E TRACCIAMENTO',
      privacyCookiesText: 'Il nostro sito non utilizza alcuno strumento di tracciamento dei visitatori, servizi di analisi o cookie di terze parti. I caratteri tipografici usati per la grafica del sito vengono caricati da Google Fonts: durante il caricamento di una pagina Google potrebbe quindi ricevere il Suo indirizzo IP. Noi non abbiamo accesso a questi dati e non li utilizziamo.',
      license: 'Licenza della struttura',
      licenseText: 'Villa Aristotle opera nel rispetto della normativa greca sulle strutture ricettive e dispone delle licenze di esercizio richieste dalla legge greca.',
    },
    // Rules
    rules: {
      title: 'Regolamento della casa',
      checkInLabel: 'Check-in',
      checkInTime: 'Dalle 16:00.',
      checkOutLabel: 'Check-out',
      checkOutTime: '09:30 – 10:00.',
      childrenLabel: 'Bambini',
      childrenText: 'Sono benvenuti bambini di tutte le età.',
      cribsLabel: 'Culle',
      cribsText: 'Disponibili gratuitamente su richiesta.',
      smokingLabel: 'Fumo',
      smokingText: 'Non consentito all\'interno.',
      partiesLabel: 'Feste',
      partiesText: 'Non sono ammesse feste o eventi.',
      quietHoursLabel: 'Ore di silenzio',
      quietHoursTime: '23:00 – 08:00.',
      petsLabel: 'Animali domestici',
      petsText: 'Fino a 1 animale domestico benvenuto, senza costi aggiuntivi.',
      parkingLabel: 'Parcheggio',
      parkingText: 'Parcheggio gratuito per un veicolo. Per un secondo veicolo il costo è di 5 €/notte.',
    },
    // Footer
    footer: {
      tagline: 'Villa privata con giardino, vicino al Centro Storico di Corfù e alle spiagge.',
      quickLinks: 'Link rapidi',
      contact: 'Contatti',
      followUs: 'Seguici',
      rights: 'Tutti i diritti riservati.',
      privacy: 'Informativa sulla privacy',
      terms: 'Termini e condizioni',
      cancellation: 'Politica di cancellazione',
      legal: 'Note legali',
      licensedProperty: 'Struttura autorizzata',
      licenseText: 'Villa Aristotle opera nel rispetto della normativa greca sulle strutture ricettive e dispone delle licenze di esercizio richieste dalla legge greca.',
      alsoAvailableOn: 'Disponibile anche su',
      theHost: 'La padrona di casa',
      hostText: 'Villa Aristotle è gestita da Christina, che si occupa personalmente della casa e della sua preparazione per ogni ospite, per un soggiorno confortevole e sereno a Corfù.',
    },
    // Common
    common: {
      viewAll: 'Vedi tutto',
      learnMore: 'Scopri di più',
      backToTop: 'Torna su',
      from: 'Da',
      currency: '€',
      comingSoon: 'Prossimamente',
      detailsComingSoon: 'Dettagli in arrivo',
      enquireNow: 'Richiedi informazioni',
      submitEnquiry: 'Invia richiesta',
      contactUs: 'Contattaci',
      backToHome: 'Torna alla home',
      viewDetails: 'Vedi dettagli',
      important: 'Importante',
    },
    // Reviews
    reviews: {
      title: 'Valutazioni degli ospiti',
      subtitle: 'Scopra cosa dicono gli ospiti di Villa Aristotle sulle principali piattaforme di prenotazione.',
      reviewsLabel: 'recensioni',
      superhost: 'Superhost (per la 2ª volta)',
      updated: 'Aggiornamento: ottobre 2026',
    },
    // Bedrooms
    bedrooms: {
      title: 'Camere da letto',
      subtitle: 'Villa Aristotle dispone di 4 camere da letto e ospita fino a 8 persone.',
      bedroom1: 'Camera 1',
      bedroom1Desc: 'Al piano terra, con letto matrimoniale, aria condizionata con riscaldamento e Smart TV con Netflix.',
      bedroom2: 'Camera 2',
      bedroom2Desc: 'Al piano terra, con letto matrimoniale, aria condizionata con riscaldamento e Smart TV con Netflix.',
      bedroom3: 'Camera 3',
      bedroom3Desc: 'Al piano terra, con letto matrimoniale, aria condizionata con riscaldamento e Smart TV con Netflix.',
      bedroom4: 'Camera 4',
      bedroom4Desc: 'Al piano seminterrato, con 2 letti singoli e una piccola finestra alta. La camera più fresca della casa, anche d\'estate – l\'aria fresca dei climatizzatori del piano terra scende fin lì; ventilatori a soffitto e a pavimento, senza climatizzatore proprio. Accesso aperto dal soggiorno, senza porta.',
      needInfo: 'Desidera informazioni sulla sistemazione?',
      needInfoText: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
    },
    // Villa
    villa: {
      title: 'Villa Aristotle',
      welcome: 'Benvenuti a Villa Aristotle',
      descPart1: 'Villa Aristotle è una villa privata con giardino a Corfù città, a 10 minuti dall\'aeroporto e dal porto, vicino al {unesco}.',
      unescoLink: 'Centro Storico di Corfù (Patrimonio Mondiale UNESCO)',
      descPart2: 'La casa ospita fino a 8 persone e dispone di 4 camere da letto, cucina completamente attrezzata, accoglienti spazi interni e un giardino privato di 300 m² con zone relax all\'aperto.',
      descPart3: 'La sua posizione unisce il facile accesso al Centro Storico di Corfù e alle spiagge a un ambiente tranquillo per il soggiorno.',
      quickEnquiry: 'Prenotazione diretta',
      quickEnquiryText: 'Prenotazione diretta. Il prezzo che vede è quello finale (CRF inclusa). Conferma dalla padrona di casa entro 24 ore. Non Le viene addebitato nulla prima della conferma.',
      interiorPhoto: 'Interni',
      exteriorPhoto: 'Esterni',
      viewAccommodation: 'Vedi i dettagli della sistemazione',
      discoverAvailable: 'Scopra i servizi disponibili',
      exploreCorfu: 'Esplori Corfù, Grecia',
      bedrooms: '4 camere da letto',
      maxGuests: 'Fino a 8 ospiti',
    },
    // Host
    host: {
      title: 'La padrona di casa',
      textPart1: 'Mi chiamo Christina e mi occupo personalmente di Villa Aristotle, perché ogni ospite possa godersi un soggiorno confortevole, curato e sereno a Corfù.',
      textPart2: 'Per me ospitare significa attenzione personale, cura dei dettagli e un\'esperienza di soggiorno autentica.',
    },
    // Toasts (μηνύματα σφάλματος που ήταν hardcoded στα components)
    toasts: {
      fillRequired: 'Compili tutti i campi obbligatori',
      datesUnavailable: 'Le date selezionate non sono disponibili. Ne scelga altre.',
      someDatesUnavailable: 'Alcune date del periodo selezionato non sono disponibili. Ne scelga altre.',
      bookingError: 'Si è verificato un errore durante l\'invio della richiesta. Riprovi.',
      contactError: 'Si è verificato un errore durante l\'invio del messaggio. Riprovi.',
    },
    // WhatsApp
    whatsapp: {
      message: 'Buongiorno! Vorrei informazioni su Villa Aristotle 🌿\nDate: \nOspiti: ',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      villa: 'La Villa',
      bedrooms: 'Chambres',
      amenities: 'Équipements',
      gallery: 'Galerie',
      booking: 'Réserver',
      reviews: 'Avis',
      location: 'Situation',
      contact: 'Contact',
      policies: 'Conditions',
      more: 'Plus',
      host: 'Votre hôtesse',
    },
    // Hero
    hero: {
      subtitle: 'Corfou, Grèce',
      title: 'Villa Aristotle',
      tagline: 'Villa privée avec jardin, près de la Vieille Ville de Corfou et des plages.',
      proximity: '10 min de l\'aéroport et du port • À Corfou-ville, à deux pas de la {unesco}',
      unescoLink: 'Vieille Ville de Corfou',
    },
    // Sections
    sections: {
      welcomeTitle: 'Bienvenue à la Villa Aristotle',
      welcomeText: 'Située sur l\'île enchanteresse de Corfou, la Villa Aristotle offre une escapade paisible dans l\'une des destinations les plus appréciées de Grèce.',
      featuresTitle: 'La propriété',
      featuresSubtitle: 'Ce qui vous attend',
      reviewsTitle: 'Expériences de nos voyageurs',
      reviewsSubtitle: 'Ce que nos voyageurs disent de leur séjour',
      locationTitle: 'Emplacement privilégié',
      locationSubtitle: 'Corfou, Grèce',
      ctaTitle: 'Renseignez-vous sur votre séjour',
      ctaText: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
    },
    // Features
    features: {
      spaciousVilla: 'Villa spacieuse',
      spaciousVillaDesc: 'Hébergement confortable',
      bedrooms: 'Plusieurs chambres',
      bedroomsDesc: 'Jusqu\'à 8 voyageurs',
      location: 'Situation à Corfou',
      locationDesc: 'Cadre insulaire grec',
    },
    // Booking Widget & Page
    booking: {
      checkIn: 'Arrivée',
      checkOut: 'Départ',
      guests: 'Voyageurs',
      guest: 'Voyageur',
      selectDate: 'Choisir une date',
      checkAvailability: 'Vérifier les disponibilités',
      requestBooking: 'Réserver',
      perNight: 'par nuit',
      nights: 'nuits',
      night: 'nuit',
      petQuestion: 'Voyagez-vous avec un animal ?',
      petNote: 'Nous acceptons 1 animal maximum, sans frais supplémentaires.',
      petFee: 'Animal',
      yes: 'Oui',
      no: 'Non',
      petRequired: 'Veuillez indiquer si vous voyagez avec un animal (Oui / Non).',
      vehicleQuestion: 'Deuxième véhicule (5 €/nuit)',
      vehicleFee: 'Deuxième véhicule',
      total: 'Prix final',
      envFee: 'Taxe de résilience climatique (CRF)',
      envFeeShort: 'CRF',
      infoLabel: 'Plus d\'informations',
      envFeeInfo: 'Taxe d\'État pour la résilience face à la crise climatique. 15 €/nuit d\'avril à octobre, 4 €/nuit de novembre à mars. Obligatoire par la loi.',
      vehicleInfo: 'Le stationnement est gratuit pour un véhicule. Un deuxième véhicule est facturé 5 €/nuit.',
      priceInfo: 'Le tarif correspond à la nuitée. La CRF et les éventuels services supplémentaires sont indiqués séparément. Pour les nuitées du 26/10 au 31/10/2026, du 16/11 au 10/12/2026 et du 08/01 au 28/02/2027 : −15 % pour 1–2 personnes, −10 % pour 3–4 personnes.',
      lengthDiscount: 'Remise pour long séjour',
      scarcityOne: 'Plus qu\'1 jour disponible en',
      scarcityMany: 'Plus que {n} jours disponibles en',
      discount: 'Remise',
      discountCode: 'Code de réduction',
      apply: 'Appliquer',
      applied: 'Appliqué !',
      addOns: 'Services et expériences',
      yourDetails: 'Vos coordonnées',
      fullName: 'Nom et prénom',
      email: 'E-mail',
      phone: 'Téléphone',
      message: 'Demandes particulières (facultatif)',
      submit: 'Réserver',
      submitting: 'Envoi en cours…',
      success: 'Votre demande de réservation a bien été envoyée !',
      successMessage: 'Nous vous contacterons sous 24 heures pour confirmer votre réservation.',
      enquiryNotice: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      airportNotice: 'Remarque : l\'aéroport de Corfou sera fermé du 20 au 28 janvier 2027 pour travaux. Merci d\'en tenir compte lors de la planification de votre arrivée.',
      minStayError: 'Le séjour minimum pour ces dates est de {n} nuits.',
      sameDayError: 'Les réservations pour le jour même ne sont possibles que jusqu\'à 10h00 (heure grecque). Merci de choisir une date d\'arrivée ultérieure.',
      maxGuests: '8 voyageurs maximum (9 avec canapé-lit sur demande)',
      bookYourStay: 'Réservez votre séjour',
      bookingSubtitle: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      selectYourDates: 'Choisissez vos dates',
      priceSummary: 'Récapitulatif du tarif',
      selectDatesToSeePricing: 'Choisissez vos dates pour voir les tarifs',
      season: 'Saison',
      yourBookingEnquiry: 'Votre demande de réservation',
      dates: 'Dates',
      estimatedTotal: 'Prix final',
      subjectToConfirmation: 'confirmation sous 24 heures',
      backToHome: 'Retour à l\'accueil',
      noChargeYet: 'Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      noChargeYetLong: 'Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      enterCode: 'Saisir le code',
      discountAppliedText: 'remise appliquée',
      discountCodeLabel: 'Code de réduction (facultatif)',
      discountInvalid: 'Ce code de réduction n\'est pas valide. Veuillez le corriger ou laisser le champ vide.',
      priceBeforeDiscount: 'Prix avant remise',
      discountCodeLine: 'Code {code}',
      enquireNow: 'Réserver maintenant',
      checkAvailabilityShort: 'Vérifier les disponibilités',
      wonNotChargedYet: 'Aucun montant ne vous sera encore débité',
      specialRequestsPlaceholder: 'Demandes particulières, heure d\'arrivée, etc.',
    },
    // Contact
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Nous serions ravis de vous lire',
      name: 'Votre nom',
      email: 'Adresse e-mail',
      phone: 'Numéro de téléphone',
      subject: 'Objet',
      message: 'Votre message',
      send: 'Envoyer le message',
      sending: 'Envoi en cours…',
      address: 'Adresse',
      success: 'Votre message a bien été envoyé !',
      addressStreet: 'Toni Spiteri 11',
      addressCity: 'Corfou-ville 49100',
      addressCountry: 'Grèce',
      responseTime: 'Nous répondons généralement sous 24 heures.',
      contactInfo: 'Coordonnées',
      responseTimeLabel: 'Délai de réponse',
      responseTimeText: 'Nous répondons généralement sous 24 heures.',
      thankYou: 'Merci de nous avoir contactés. Nous répondrons à votre message sous 24 heures.',
    },
    // Gallery
    gallery: {
      title: 'Galerie photos',
      subtitle: 'Découvrez les photos de la Villa Aristotle et de ses espaces intérieurs et extérieurs.',
      exterior: 'Extérieur',
      garden: 'Jardin',
      terrace: 'Terrasse',
      livingRoom: 'Salon',
      kitchen: 'Cuisine',
      bedroom: 'Chambre',
    },
    // Amenities
    amenities: {
      title: 'Équipements de la Villa Aristotle',
      internetTitle: 'Internet et divertissement',
      wifi: 'Wi-Fi gratuit dans toute la maison',
      netflix: 'Netflix',
      smartTv: 'Smart TV',
      tvBedroom: 'Télévision dans chaque chambre',
      workDesk: 'Bureau',
      accommodationTitle: 'Hébergement',
      fourBedrooms: '4 chambres',
      eightGuests: 'Jusqu\'à 8 voyageurs',
      linens: 'Linge de lit',
      towels: 'Serviettes',
      travelCots: '2 lits parapluie pour bébés',
      kitchenTitle: 'Cuisine',
      fullyEquipped: 'Cuisine entièrement équipée',
      fridge: 'Réfrigérateur',
      oven: 'Four',
      stove: 'Plaques de cuisson',
      coffeeMaker: 'Machine Nespresso (capsules), café filtre et café grec',
      kettle: 'Bouilloire',
      cookware: 'Ustensiles de cuisine',
      bathroomTitle: 'Salle de bain',
      privateBathrooms: '2 salles de bain (une avec baignoire, une avec douche)',
      showerBath: 'Douche ou baignoire',
      hairDryer: 'Sèche-cheveux',
      toiletries: 'Produits de toilette',
      outdoorTitle: 'Espaces extérieurs',
      privateGarden: 'Jardin privé (300 m²)',
      terrace: 'Terrasse / balcon',
      outdoorSeating: 'Coin détente extérieur',
      bbq: 'Barbecue',
      practicalTitle: 'Équipements pratiques',
      parking: 'Stationnement gratuit pour un véhicule (deuxième véhicule 5 €/nuit)',
      safe: 'Coffre-fort',
      washingMachine: 'Lave-linge',
      dryer: 'Sèche-linge',
      airConditioning: 'Climatisation réversible dans les 3 chambres du rez-de-chaussée ; radiateurs dans toute la maison',
      heating: 'Chauffage',
      gym: 'Petite salle de sport (tapis de course, haltères, élastiques)',
      keyBox: 'Boîte à clés – arrivée autonome à toute heure',
      pet: 'Animal accepté (1 maximum, gratuit)',
    },
    // Location
    location: {
      title: 'Corfou, Grèce',
      subtitle: 'La Villa Aristotle se trouve sur la magnifique île de Corfou, l\'une des destinations ioniennes les plus enchanteresses de Grèce. À 10 minutes en voiture de l\'aéroport (CFU) et du port, à 10 minutes en voiture de la vieille ville (UNESCO).',
      byAir: 'En avion',
      byAirDesc: 'Aéroport international de Corfou (CFU)',
      byAirText: 'Vols directs depuis les grandes villes européennes.',
      byCar: 'En voiture',
      byCarDesc: 'Location recommandée',
      byCarText: 'Nous vous recommandons de louer une voiture pour explorer Corfou. Nous pouvons vous aider à organiser la location auprès de prestataires locaux.',
      aboutCorfu: 'Découvrez Corfou',
      aboutCorfuText: 'Corfou (Kerkyra) est l\'une des plus belles îles grecques, réputée pour ses paysages verdoyants, son architecture vénitienne et son littoral spectaculaire.',
      gettingHere: 'Comment venir',
      aboutTheIsland: 'À propos de l\'île',
      discoverCorfuText: 'Corfou (Kerkyra) est l\'une des plus belles îles grecques, réputée pour ses paysages verdoyants, son architecture vénitienne et son littoral spectaculaire. L\'île allie à merveille beauté naturelle, histoire riche et culture vivante.',
      oldTownText: 'La Vieille Ville, inscrite au patrimoine mondial de l\'UNESCO, abrite d\'imposantes forteresses, des ruelles étroites et une architecture d\'influence italienne. L\'île est réputée pour son excellente cuisine locale, ses eaux cristallines et son accueil chaleureux.',
      openInMaps: 'Ouvrir dans Google Maps',
    },
    // Policies
    policies: {
      title: 'Conditions de réservation',
      subtitle: 'Merci de lire nos conditions avant de réserver.',
      terms: 'Conditions générales',
      bookingProcess: 'Processus de réservation',
      bookingProcessText: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      paymentTerms: 'Conditions de paiement',
      paymentTermsText: 'Les instructions de paiement vous sont envoyées avec la confirmation de réservation.',
      checkInOut: 'Arrivée / Départ',
      checkIn: 'Arrivée : 16h00',
      checkOut: 'Départ : jusqu\'à 10h00',
      cancellation: 'Conditions d\'annulation',
      cancellationText: 'Annulation gratuite jusqu\'à 60 jours avant l\'arrivée (remboursement intégral). En cas d\'annulation dans les 60 jours précédant l\'arrivée, le montant n\'est pas remboursé.',
      modifications: 'Modifications',
      modificationsText: 'Les changements de dates sont soumis à disponibilité. Merci de nous contacter le plus tôt possible si vous souhaitez modifier votre réservation.',
      damageDeposit: 'Caution',
      damageDepositText: 'Une caution de 500 € est demandée ; elle est restituée sous 7 jours après le départ.',
      houseRules: 'Règlement intérieur',
      houseRulesText: 'Le règlement détaillé vous sera remis avec la confirmation de réservation. En règle générale : interdiction de fumer à l\'intérieur et respect des heures de calme.',
      noSmoking: 'Interdiction de fumer à l\'intérieur',
      noParties: 'Aucune fête sans accord préalable',
      quietHours: 'Heures de calme : 23h00 – 08h00',
      maxOccupancy: 'Capacité maximale : 8 voyageurs (9 avec canapé-lit sur demande)',
      privacy: 'POLITIQUE DE CONFIDENTIALITÉ — VILLA ARISTOTLE',
      privacyLastUpdated: 'Dernière mise à jour : 05/08/2026',
      privacyWhoWeAre: '1. QUI SOMMES-NOUS',
      privacyWhoWeAreText: 'La Villa Aristotle se trouve à Corfou-ville, en Grèce. Pour toute question relative à vos données personnelles, écrivez à : villa.aristotle.cfu@gmail.com',
      privacyWhatWeCollect: '2. QUELLES DONNÉES NOUS COLLECTONS',
      privacyWhatWeCollectText: 'Lorsque vous envoyez une demande de réservation via notre site, nous collectons : nom, e-mail, téléphone, dates de séjour, nombre de voyageurs, la présence éventuelle d\'un animal et un champ facultatif pour les demandes particulières. Nous ne collectons aucune donnée de paiement via le site : le paiement s\'effectue hors du site, après que nous avons confirmé personnellement votre réservation.',
      privacyWhyWeCollect: '3. POURQUOI NOUS LES COLLECTONS',
      privacyWhyWeCollectText: 'Uniquement pour : le traitement de votre réservation, la communication avant et pendant votre séjour, et le respect des obligations fiscales (AADE) lorsque cela est requis.',
      privacyWhoHasAccess: '4. QUI Y A ACCÈS',
      privacyWhoHasAccessText: 'Vos données ne sont ni vendues ni communiquées à des tiers à des fins publicitaires. Les outils de gestion des réservations (channel manager) sont utilisés uniquement pour organiser votre séjour.',
      privacyYourRights: '5. VOS DROITS',
      privacyYourRightsText: 'Vous disposez d\'un droit d\'accès, de rectification et de suppression de vos données, ainsi que du droit de vous opposer à leur traitement, en écrivant à l\'adresse e-mail ci-dessus. La Villa Aristotle s\'engage à protéger vos données personnelles conformément au Règlement général sur la protection des données (RGPD).',
      privacyCookies: '6. COOKIES ET SUIVI',
      privacyCookiesText: 'Notre site n\'utilise aucun outil de suivi des visiteurs, service d\'analyse ni cookie tiers. Les polices de caractères utilisées pour l\'affichage du site sont chargées depuis Google Fonts : lors du chargement d\'une page, Google peut donc recevoir votre adresse IP. Nous n\'avons pas accès à ces données et ne les utilisons pas.',
      license: 'Licence de l\'établissement',
      licenseText: 'La Villa Aristotle est exploitée conformément à la réglementation grecque sur les hébergements touristiques et dispose des licences d\'exploitation requises par la loi grecque.',
    },
    // Rules
    rules: {
      title: 'Règlement intérieur',
      checkInLabel: 'Arrivée',
      checkInTime: 'À partir de 16h00.',
      checkOutLabel: 'Départ',
      checkOutTime: '09h30 – 10h00.',
      childrenLabel: 'Enfants',
      childrenText: 'Les enfants de tous âges sont les bienvenus.',
      cribsLabel: 'Lits bébé',
      cribsText: 'Disponibles gratuitement sur demande.',
      smokingLabel: 'Tabac',
      smokingText: 'Interdit à l\'intérieur.',
      partiesLabel: 'Fêtes',
      partiesText: 'Les fêtes et événements ne sont pas autorisés.',
      quietHoursLabel: 'Heures de calme',
      quietHoursTime: '23h00 – 08h00.',
      petsLabel: 'Animaux',
      petsText: '1 animal maximum bienvenu, sans frais supplémentaires.',
      parkingLabel: 'Stationnement',
      parkingText: 'Stationnement gratuit pour un véhicule. Un deuxième véhicule est facturé 5 €/nuit.',
    },
    // Footer
    footer: {
      tagline: 'Villa privée avec jardin, près de la Vieille Ville de Corfou et des plages.',
      quickLinks: 'Liens rapides',
      contact: 'Contact',
      followUs: 'Suivez-nous',
      rights: 'Tous droits réservés.',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions générales',
      cancellation: 'Conditions d\'annulation',
      legal: 'Mentions légales',
      licensedProperty: 'Établissement agréé',
      licenseText: 'La Villa Aristotle est exploitée conformément à la réglementation grecque sur les hébergements touristiques et dispose des licences d\'exploitation requises par la loi grecque.',
      alsoAvailableOn: 'Également disponible sur',
      theHost: 'Votre hôtesse',
      hostText: 'La Villa Aristotle est gérée par Christina, qui s\'occupe personnellement de la maison et de sa préparation pour chaque voyageur, pour un séjour confortable et paisible à Corfou.',
    },
    // Common
    common: {
      viewAll: 'Tout voir',
      learnMore: 'En savoir plus',
      backToTop: 'Haut de page',
      from: 'À partir de',
      currency: '€',
      comingSoon: 'Bientôt disponible',
      detailsComingSoon: 'Détails à venir',
      enquireNow: 'Faire une demande',
      submitEnquiry: 'Envoyer la demande',
      contactUs: 'Nous contacter',
      backToHome: 'Retour à l\'accueil',
      viewDetails: 'Voir les détails',
      important: 'Important',
    },
    // Reviews
    reviews: {
      title: 'Avis des voyageurs',
      subtitle: 'Découvrez ce que disent les voyageurs de la Villa Aristotle sur les principales plateformes de réservation.',
      reviewsLabel: 'avis',
      superhost: 'Superhost (2e fois)',
      updated: 'Mise à jour : octobre 2026',
    },
    // Bedrooms
    bedrooms: {
      title: 'Chambres',
      subtitle: 'La Villa Aristotle dispose de 4 chambres et accueille jusqu\'à 8 voyageurs.',
      bedroom1: 'Chambre 1',
      bedroom1Desc: 'Au rez-de-chaussée, avec lit double, climatisation réversible et Smart TV avec Netflix.',
      bedroom2: 'Chambre 2',
      bedroom2Desc: 'Au rez-de-chaussée, avec lit double, climatisation réversible et Smart TV avec Netflix.',
      bedroom3: 'Chambre 3',
      bedroom3Desc: 'Au rez-de-chaussée, avec lit double, climatisation réversible et Smart TV avec Netflix.',
      bedroom4: 'Chambre 4',
      bedroom4Desc: 'En rez-de-jardin, avec 2 lits simples et une petite fenêtre haute. La chambre la plus fraîche de la maison, même en été – l\'air frais des climatiseurs du rez-de-chaussée y descend ; ventilateurs de plafond et sur pied, sans climatiseur propre. Accès ouvert depuis le salon, sans porte.',
      needInfo: 'Besoin d\'informations sur l\'hébergement ?',
      needInfoText: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
    },
    // Villa
    villa: {
      title: 'Villa Aristotle',
      welcome: 'Bienvenue à la Villa Aristotle',
      descPart1: 'La Villa Aristotle est une villa privée avec jardin à Corfou-ville, à 10 minutes de l\'aéroport et du port, près de la {unesco}.',
      unescoLink: 'Vieille Ville de Corfou (patrimoine mondial de l\'UNESCO)',
      descPart2: 'La maison accueille jusqu\'à 8 voyageurs et dispose de 4 chambres, d\'une cuisine entièrement équipée, d\'espaces intérieurs confortables et d\'un jardin privé de 300 m² avec des coins détente en plein air.',
      descPart3: 'Sa situation allie un accès facile à la Vieille Ville de Corfou et aux plages à un environnement calme pour le séjour.',
      quickEnquiry: 'Réservation directe',
      quickEnquiryText: 'Réservation directe. Le prix affiché est le prix final (CRF comprise). Confirmation par votre hôtesse sous 24 heures. Aucun débit avant la confirmation.',
      interiorPhoto: 'Intérieur',
      exteriorPhoto: 'Extérieur',
      viewAccommodation: 'Voir les détails de l\'hébergement',
      discoverAvailable: 'Découvrez les équipements',
      exploreCorfu: 'Explorez Corfou, Grèce',
      bedrooms: '4 chambres',
      maxGuests: 'Jusqu\'à 8 voyageurs',
    },
    // Host
    host: {
      title: 'Votre hôtesse',
      textPart1: 'Je m\'appelle Christina et je m\'occupe personnellement de la Villa Aristotle, afin que chaque voyageur profite d\'un séjour confortable, soigné et paisible à Corfou.',
      textPart2: 'Pour moi, recevoir signifie attention personnelle, souci du détail et une expérience de séjour authentique.',
    },
    // Toasts (μηνύματα σφάλματος που ήταν hardcoded στα components)
    toasts: {
      fillRequired: 'Merci de remplir tous les champs obligatoires',
      datesUnavailable: 'Les dates sélectionnées ne sont pas disponibles. Merci d\'en choisir d\'autres.',
      someDatesUnavailable: 'Certaines dates de la période sélectionnée ne sont pas disponibles. Merci d\'en choisir d\'autres.',
      bookingError: 'Une erreur s\'est produite lors de l\'envoi de votre demande. Merci de réessayer.',
      contactError: 'Une erreur s\'est produite lors de l\'envoi de votre message. Merci de réessayer.',
    },
    // WhatsApp
    whatsapp: {
      message: 'Bonjour ! Je souhaiterais des informations sur la Villa Aristotle 🌿\nDates : \nVoyageurs : ',
    },
  },
};

const LanguageContext = createContext();

// Εσωτερικοί κωδικοί γλώσσας. Το 'gr' διατηρείται ως έχει (αντί για το σωστό ISO 'el')
// ώστε να μη σπάσουν το localStorage των υπαρχόντων χρηστών και οι κρατήσεις στη βάση.
export const LANGUAGES = ['en', 'gr', 'de', 'it', 'fr'];

// Εσωτερικός κωδικός → ISO κωδικός (για το lang attribute, τα hreflang και τα locales)
export const ISO_LANG = { en: 'en', gr: 'el', de: 'de', it: 'it', fr: 'fr' };

// ISO κωδικός → εσωτερικός κωδικός, για το ?lang= query param
const FROM_ISO = { en: 'en', el: 'gr', gr: 'gr', de: 'de', it: 'it', fr: 'fr' };

const resolveInitialLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  // Το ?lang= έχει προτεραιότητα ώστε τα hreflang URLs να ανοίγουν στη σωστή γλώσσα
  try {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (param && FROM_ISO[param.toLowerCase()]) return FROM_ISO[param.toLowerCase()];
  } catch (e) {
    // αγνοούμε· πέφτουμε στο localStorage
  }
  const stored = localStorage.getItem('villa-language');
  return LANGUAGES.includes(stored) ? stored : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(resolveInitialLanguage);

  useEffect(() => {
    localStorage.setItem('villa-language', language);
    document.documentElement.lang = ISO_LANG[language] || 'en';
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    // Fallback στα Αγγλικά αν λείπει κλειδί σε κάποια γλώσσα, αντί για το raw key
    if (value == null && language !== 'en') {
      let fallback = translations.en;
      for (const k of keys) {
        fallback = fallback?.[k];
      }
      value = fallback;
    }
    return value || key;
  };

  // Κυκλική εναλλαγή EN → GR → DE → EN
  const toggleLanguage = () => {
    setLanguage((prev) => LANGUAGES[(LANGUAGES.indexOf(prev) + 1) % LANGUAGES.length]);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
